import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Row, Col, Typography, Tag, Card,
  Carousel, Form, InputNumber, Button, Divider,
  App, Modal, Table, Grid,
} from 'antd';
import {
  ArrowLeftOutlined, WhatsAppOutlined, DollarOutlined,
  PlayCircleOutlined, PictureOutlined,
  LeftOutlined, RightOutlined,
  TrophyOutlined, SearchOutlined, FullscreenOutlined, StarFilled,
} from '@ant-design/icons';
import { micrositioService, ofertaService, buyerService, analyticsService, fixUrl } from '../../services/api';
import { getShareUrl } from '../../services/shareUrls';
import { SiteHeader, SiteFooter } from './RematePage';
import { marcarDeepLinkConsumido } from '../../utils/deepLink';
import { formatearMonto, parsearMonto } from '../../utils/montoInput';
import {
  enEscalon as enEscalonBase,
  escalonSiguiente as escalonSiguienteBase,
} from '../../utils/escalonOferta';
import { heroBackground, heroTextLayout, heroTextPadding, heroMargenSuperior } from '../../utils/hero';
import { escalonDe } from '../../utils/depsEscalones';
import PujaSpinner from '../../components/PujaSpinner';
import CountdownPreOfertas from '../../components/CountdownPreOfertas';
import AuthContext from '../../context/AuthContext';
import { TIPOGRAFIAS, altoHeaderDe, terminoLote } from '../../utils/micrositioTema';
import { buildAnimalSlug, imagenAnimalUrl } from '../../utils/animalMedia';
import { useAvisarAltoAlPadre } from '../../hooks/useAvisarAltoAlPadre';
import { useCentroVisibleDelPadre } from './useCentroVisibleDelPadre';
import { urlSegura } from '../../utils/urlSegura';
import { htmlSanitizado } from '../../utils/htmlSeguro';

const { Title, Text } = Typography;

// Momento de apertura/cierre de las pre-ofertas, escrito para leer.
// Se fuerza el huso de Argentina: el backend manda ISO con offset y el
// visitante puede tener el reloj en cualquier lado, pero el remate es acá.
const fmtMomento = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  return new Intl.DateTimeFormat('es-AR', {
    weekday: 'long', day: '2-digit', month: 'long',
    hour: '2-digit', minute: '2-digit',
    timeZone: 'America/Argentina/Buenos_Aires',
  }).format(d).replace(',', '');
};

// Fecha ISO (yyyy-mm-dd) → dd-mm-yy (año 2 dígitos). '—' si no hay.
const fmtFecha = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return (y && m && d) ? `${d}-${m}-${y.slice(-2)}` : iso;
};

// La caravana viene por NOMBRE en español (Celeste, Roja...) → color CSS.
const CARAVANA_COLORES = {
  celeste: '#7ec8e3', roja: '#e53935', rojo: '#e53935', rosa: '#ec407a',
  verde: '#43a047', amarilla: '#fdd835', amarillo: '#fdd835', azul: '#1e88e5',
  blanca: '#ffffff', blanco: '#ffffff', negra: '#212121', negro: '#212121',
  naranja: '#fb8c00', violeta: '#8e24aa', fucsia: '#d81b60', gris: '#9e9e9e',
};
const colorCaravana = (nombre) => {
  if (!nombre) return null;
  const key = String(nombre).trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  return CARAVANA_COLORES[key] || nombre; // si no está en el mapa, se usa tal cual (por si ya es un hex)
};

// ── Tipos de visualización (1=Toro, 2=Hembra c/servicio, 3=Hembra c/cría, 4=Hembra básica, 5=Hembra parida c/servicio)
const VIS_TORO = 1, VIS_HEMBRA_SERVICIO = 2, VIS_HEMBRA_CRIA = 3, VIS_HEMBRA_BASICA = 4, VIS_HEMBRA_PARIDA_SERVICIO = 5;

// ═════════════════════════════════════════════════════════════════════
const AnimalPage = () => {
  // Le avisa al sitio incrustante cuánto medimos, para que agrande el
  // iframe en vez de dejarlo clavado en los 800px del snippet.
  useAvisarAltoAlPadre();

  const { slug, animalSlug } = useParams();
  // Espejo del slug para que las respuestas en vuelo puedan preguntar en QUÉ
  // ficha estamos AHORA, y no en cuál estábamos cuando salió el pedido.
  const animalSlugRef = useRef(animalSlug);
  animalSlugRef.current = animalSlug;
  const navigate = useNavigate();
  // `user` ademas de `isAuthenticated`: la tarjeta de pre-oferta compara el
  // id contra el de la mejor oferta para avisar cuando alguien esta por
  // superarse a si mismo.
  const { isAuthenticated, user } = useContext(AuthContext);
  const { message } = App.useApp();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  // Hay un POST de oferta viajando. El countdown lo mira para no recargar la
  // pagina encima de alguien que oferto sobre la hora y todavia no sabe si su
  // oferta entro. Va acá arriba con el resto de los hooks: mas abajo quedaba
  // despues de los early returns de `loading` y `!data`, y eso rompe el orden
  // de los hooks entre renders.
  const [ofertaEnVuelo, setOfertaEnVuelo] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    // Reset al navegar entre animales (prev/next o link a otro animal del
    // mismo catalogo): mostrar spinner inmediatamente para que el usuario
    // tenga feedback visual. Sin esto el componente quedaba mostrando la
    // pagina vieja durante el fetch del nuevo animal (2-3s) y parecia
    // que el click no habia hecho nada.
    setLoading(true);
    setData(null);
    window.scrollTo({ top: 0, behavior: 'auto' });
    const cargar = async () => {
      try {
        const res = await micrositioService.getAnimal(slug, animalSlug);
        // Los nombres de categoría (ficha + prev/next) ya vienen con el alias
        // aplicado desde el backend (micrositio_routes). No se toca acá.
        setData(res.data);
        const idAnimal = parseInt(animalSlug?.split('-')[0], 10);
        const idEvento = res.data?.evento?.id_evento;
        // Optional chaining: sin esto, un payload sin `evento` convierte un
        // fetch EXITOSO en "no pudimos cargar la ficha" + navigate() afuera.
        if (!isNaN(idAnimal) && idEvento) {
          // catch vacío a propósito: el tracking es fire-and-forget. Si falla
          // analytics, la persona igual tiene que poder ver la ficha — molestarla
          // con un error por una métrica sería peor que perder la métrica.
          analyticsService.track('visita_animal', idEvento, {
            id_animal: idAnimal,
          }).catch(() => {});
        }
      } catch (err) {
        message.error(err?.response?.data?.error || 'No pudimos cargar la ficha del animal');
        navigate(`/remate/${slug}`);
      } finally {
        setLoading(false);
      }
    };
    cargar();
    // navigate viene de useNavigate() — referencia estable de react-router
    // `message` (de App.useApp()) queda fuera a proposito: es estable durante
    // la vida del componente e incluirla no cambia nada, pero re-dispararia
    // la carga de la ficha en cada render del provider de antd.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, animalSlug, navigate]);

  if (loading) {
    return <PujaSpinner size="lg" label="Cargando ficha…" fondo="#fff" />;
  }

  if (!data) return null;

  const {
    animal, imagenes, familiares, genealogia, companeros_corral,
    evento, prev_animal, next_animal, categorias = [], razas = [], cabanas = [],
    mejor_oferta = null,
    cria = null,
    ofertas = [],
  } = data;
  // TODAS las crías al pie (2026-10-21, las elecciones). `cria` suelta es la
  // del backend anterior: mientras el front sale solo al pushear y el backend
  // se despliega a mano, conviven un rato.
  const crias = data.crias ?? (cria ? [cria] : []);

  // El montaje sigue atado a `ofertas_activas`, NO a estado_ofertas: este ultimo
  // devuelve 'inactivas' tambien cuando el evento esta finalizado, y montar por
  // ahi hacia desaparecer el cartel de "evento finalizado" que la tarjeta ya
  // sabia mostrar. El caso "hay fechas pero el switch quedo apagado" se avisa en
  // el ORIGEN -el form del evento lo marca en amarillo- que es donde se puede
  // corregir, en vez de intentar explicarle una desconfiguracion al comprador.
  const hayPreOfertas = evento.ofertas_activas === 1;

  // PreOfertaCard lleva su propio `mejorOferta` local, pero el historial sale de
  // `data.ofertas`. Sin esto quedaban dos paneles lado a lado contradiciendose
  // sobre la plata: la tarjeta mostrando la oferta nueva y el historial sin
  // ella. Peor todavia con historial vacio, porque TablaOfertas devuelve null y
  // el panel directamente no aparece despues de la primera oferta del remate.
  // Se inserta ORDENANDO por monto, no al principio: `data.ofertas` viene
  // ordenada desc por monto desde el backend y la fila 0 se pinta como "mejor
  // oferta" (.oferta-top-row). Cuando la oferta entra pero NO lidera —otra mas
  // alta se metio en el medio, el backend responde lidera:false— un prepend a
  // ciegas la clavaba arriba de todo y la mostraba como ganadora, justo al lado
  // de la tarjeta que decia otra cosa. El camino feliz lo tapaba: mientras
  // liderabas, prepend y ordenar dan lo mismo.
  const agregarOfertaAlHistorial = (oferta, lidera = true) => {
    if (!oferta) return;

    // NO LIDERA: no se inserta nada localmente, se relee la ficha entera.
    //
    // Existe al menos una oferta MAS ALTA que este navegador nunca vio —entro
    // entre la validacion y el commit— asi que insertar la propia la dejaria en
    // la posicion 0, y la fila 0 se pinta como ganadora (.oferta-top-row). Si
    // ademas el refetch fallara, quedaria coronada la oferta PERDEDORA al lado
    // del modal que dice "te superaron". Un historial al que le falta una fila
    // por unos segundos es mucho menos grave que uno que miente sobre quien va
    // ganando la plata.
    if (!lidera) {
      recargarOfertas();
      return;
    }

    // LIDERA: la propia oferta entra al toque para que la respuesta se sienta
    // inmediata, ordenada y recortada a 10 como manda el backend.
    setData(prev => (prev ? {
      ...prev,
      ofertas: [...(prev.ofertas || []), oferta]
        .sort((a, b) => Number(b.monto_oferta) - Number(a.monto_oferta))
        .slice(0, 10),
    } : prev));

    // Y SIEMPRE se relee, tambien liderando. El insert local parte del historial
    // que habia en pantalla, y ese pudo quedar viejo: si mientras la persona
    // miraba entraron ofertas de otros, la lista se quedaba con las que ella
    // habia visto mas la suya, salteandose las del medio —en San Alejo se vio
    // faltando 4.600.000 y 5.200.000 entre medio de las propias—. El insert es
    // para la sensacion de inmediatez; la verdad la trae el refetch.
    recargarOfertas();
  };

  /** Relee SOLO el historial de la ficha, sin tocar el resto del estado. */
  const recargarOfertas = () => {
    const idPedido = animalSlug;
    micrositioService.getAnimal(slug, animalSlug)
      .then(res => setData(prev => (prev && idPedido === animalSlugRef.current
        ? { ...prev, ofertas: res.data?.ofertas || prev.ofertas }
        : prev)))
      // Silencioso: la tarjeta ya muestra el monto correcto que vino en la
      // respuesta de la oferta, asi que un fallo solo deja el historial un poco
      // viejo hasta el proximo refresh.
      .catch(() => {});
  };

  const config     = evento.configuracion_micrositio || {};
  const tipografia = TIPOGRAFIAS[config.tipografia_combo] || TIPOGRAFIAS.moderna;
  const tipo       = animal.visualizacion || VIS_TORO;
  const logoUrl    = evento.img_evento ? fixUrl(evento.img_evento) : null;

  const tipoCabecera       = config.cabecera || 'cabecera-1';
  // `altoHeaderDe` y no la tabla a mano: ver CondicionesPage.
  const headerHeight       = altoHeaderDe(config);
  const isTwoRow           = tipoCabecera === 'cabecera-2' || tipoCabecera === 'cabecera-3';
  // Hero desactivado en la ficha → sin banner, header sólido arriba.
  const heroOff            = !!config.hero_off_ficha;
  const colorHeaderInitial = heroOff
    ? (config.color_header || config.color_primary || '#1a5f2a')
    : (config.color_header_initial || 'transparent');
  const heroTransparente   = !heroOff && !isTwoRow && colorHeaderInitial === 'transparent';
  const configHeader       = heroOff ? { ...config, color_header_initial: colorHeaderInitial } : config;

  // Las consume `.micrositio-page .hero-zone` en index.css, que además les pone
  // el tope de mobile. Esta pantalla se había quedado con el offset inline y
  // sin override: con cabecera-3/4 arrastraba en mobile la misma franja blanca
  // que ya se había sacado del catálogo — y es donde caen los share.
  const heroVars = {
    '--hero-margin-top':  `${heroMargenSuperior(config, headerHeight, heroTransparente)}px`,
    '--hero-padding-top': `${heroTransparente ? headerHeight : 0}px`,
  };

  return (
    <div className="micrositio-page" style={{ fontFamily: tipografia.body, background: '#fff', minHeight: '100vh', ...heroVars }}>
      <SiteHeader
        config={configHeader}
        tipografia={tipografia}
        evento={evento}
        logoUrl={logoUrl}
        scrolled={scrolled}
        slug={slug}
        categorias={categorias}
        razas={razas}
        cabanas={cabanas}
        isAuthenticated={isAuthenticated}
      />

      {/* Cuenta regresiva de la ventana, colgada del header. Acá importa más
          que en el catálogo: es la pantalla donde la gente está parada cuando
          corren los últimos segundos. Descuenta contra la hora del SERVIDOR. */}
      <CountdownPreOfertas
        estadoOfertas={evento.estado_ofertas}
        top={headerHeight}
        colorPrimary={config.color_primary}
        bloquearRecarga={ofertaEnVuelo}
        // También acá, no solo en el catálogo: si la recarga automática pasa
        // con el visitante parado en la ficha, el flag queda sin marcar y el
        // deep-link se rearma igual — se lo lleva puesto en cuanto toque
        // Catálogo. El bug no desaparecía, se diferia un click.
        antesDeRecargar={marcarDeepLinkConsumido}
      />

      {heroOff ? (
        // Spacer que compensa el header fijo. El countdown NO entra en esta
        // cuenta: es una pastilla angosta que flota a la derecha y no empuja
        // contenido.
        <div className="hero-off-spacer" style={{ '--hero-off-header': `${headerHeight}px` }} />
      ) : (
        <AnimalHero
          animal={animal}
          config={config}
          tipografia={tipografia}
          heroTransparente={heroTransparente}
          fondoHero={evento.imgsec_evento ? fixUrl(evento.imgsec_evento) : null}
        />
      )}

      <div style={{ height: 5, background: config.color_primary || '#1a5f2a' }} />

      {/* ── BANNER EVENTO FINALIZADO ── */}
      {evento.es_finalizado && (
        <div style={{
          background: '#fff8e1',
          borderBottom: '2px solid #ffe082',
          padding: '18px 24px',
          textAlign: 'center',
          fontFamily: tipografia.body,
        }}>
          <span style={{ fontSize: 16, fontWeight: 600, color: '#b45309' }}>
            🏁 Evento finalizado — gracias por participar de nuestro remate
          </span>
        </div>
      )}

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 24px' }}>

        {/* NavRow fuera del grid — full width, misma altura de arranque para ambas columnas */}
        <NavRow
          slug={slug}
          prevAnimal={prev_animal}
          nextAnimal={next_animal}
          config={config}
          animal={animal}
          evento={evento}
        />

        <Row gutter={10}>
          {/* ── Columna izquierda ── */}
          <Col xs={24} lg={14}>
            <CarruselMedia
              imagenes={imagenes}
              familiares={familiares}
              videoUrl={animal.codigo_video}
              config={config}
              premio={animal.premio}
              mostrarCopa={animal.mostrar_copa}
            />

            {/* Compañeros del corral debajo del carrusel — solo vis 2 (en las
                demás visualizaciones se muestra más abajo, sin cambios). */}
            {tipo === VIS_HEMBRA_SERVICIO && companeros_corral?.length > 0 && animal.mostrar_companeros === 1 && (
              <CompanerosCorral
                companeros={companeros_corral}
                slug={slug}
                config={config}
                tipografia={tipografia}
              />
            )}

            {/* Info cards — solo mobile, debajo del carrusel */}
            <div className="animal-info-mobile">
              {tipo === VIS_HEMBRA_SERVICIO && animal.agrupado === 1 ? (
                <CardCorralAgrupado animal={animal} companeros={companeros_corral} slug={slug} config={config} tipografia={tipografia} />
              ) : (
                <>
                  <InfoNacimientoCard animal={animal} config={config} tipografia={tipografia} />
                  <InformacionActualCard animal={animal} tipo={tipo} config={config} tipografia={tipografia} />
                  {(tipo === VIS_HEMBRA_SERVICIO || tipo === VIS_HEMBRA_PARIDA_SERVICIO) && !animal.agrupado && (
                    <ServicioCard animal={animal} config={config} tipografia={tipografia} />
                  )}
                  {(tipo === VIS_HEMBRA_CRIA || tipo === VIS_HEMBRA_PARIDA_SERVICIO) && (
                    <CriasCard crias={crias} config={config} tipografia={tipografia} />
                  )}
                </>
              )}
            </div>

            {/* En vis 2 AGRUPADA el árbol y la genética se integran en la tabla
                del lote; en vis 2 individual (y demás visualizaciones) se muestran.
                Las dos elecciones del productor (árbol/tabla, tabla/gráfico) viven
                en config.genealogia_vista / config.deps_vista — ver EstructuraTab.jsx
                del panel y DEFAULT_MICROSITIO_CONFIG del backend. */}
            {genealogia && !(tipo === VIS_HEMBRA_SERVICIO && animal.agrupado === 1) && (
              config.genealogia_vista === 'tabla' ? (
                <TablaGenealogia
                  genealogia={genealogia}
                  config={config}
                  tipografia={tipografia}
                />
              ) : (
                <ArbolGenealogico
                  genealogia={genealogia}
                  config={config}
                  tipografia={tipografia}
                />
              )
            )}

            {!(tipo === VIS_HEMBRA_SERVICIO && animal.agrupado === 1) && (
              config.deps_vista === 'grafico' ? (
                <GraficoDeps animal={animal} config={config} tipografia={tipografia} />
              ) : (
                <TablaGenetica animal={animal} config={config} tipografia={tipografia} />
              )
            )}

            {/* CompanerosCorral — solo para toros/básicas agrupadas, no para hembras con tabla */}
            {companeros_corral?.length > 0 && animal.mostrar_companeros === 1 && !(tipo === VIS_HEMBRA_SERVICIO || tipo === VIS_HEMBRA_PARIDA_SERVICIO) && (
              <CompanerosCorral
                companeros={companeros_corral}
                slug={slug}
                config={config}
                tipografia={tipografia}
              />
            )}

          </Col>

          {/* ── Columna derecha — solo desktop ── */}
          <Col xs={0} lg={10}>
            <div>
              {tipo === VIS_HEMBRA_SERVICIO && animal.agrupado === 1 ? (
                <CardCorralAgrupado animal={animal} companeros={companeros_corral} slug={slug} config={config} tipografia={tipografia} />
              ) : (
                <>
                  <InfoNacimientoCard
                    animal={animal}
                    config={config}
                    tipografia={tipografia}
                  />
                  <InformacionActualCard
                    animal={animal}
                    tipo={tipo}
                    config={config}
                    tipografia={tipografia}
                  />
                  {(tipo === VIS_HEMBRA_SERVICIO || tipo === VIS_HEMBRA_PARIDA_SERVICIO) && !animal.agrupado && (
                    <ServicioCard animal={animal} config={config} tipografia={tipografia} />
                  )}
                  {(tipo === VIS_HEMBRA_CRIA || tipo === VIS_HEMBRA_PARIDA_SERVICIO) && (
                    <CriasCard crias={crias} config={config} tipografia={tipografia} />
                  )}
                </>
              )}
            </div>
          </Col>
        </Row>

        {/* Tabla del lote — full width, fuera del grid de dos columnas.
            Vis 2: tabla integrada (genealogía + servicio + genética).
            Vis 5: tabla de servicio clásica (se migrará cuando haya animales). */}
        {tipo === VIS_HEMBRA_SERVICIO && animal.agrupado === 1 && (
          <TablaLoteIntegrada
            animal={animal}
            companeros={companeros_corral}
            slug={slug}
            config={config}
            tipografia={tipografia}
          />
        )}
        {tipo === VIS_HEMBRA_PARIDA_SERVICIO && animal.agrupado === 1 && (
          <TablaServicioCorral
            animal={animal}
            companeros={companeros_corral}
            slug={slug}
            config={config}
            tipografia={tipografia}
          />
        )}

        {/* Pre-oferta + historial — dos columnas, todas las visualizaciones.
            El guard va contra null y no por truthiness: con `precio_base` en 0
            la tarjeta de pre-oferta desaparecía sin decir por qué. Es el mismo
            patrón que se arregló en la venta transitoria (commit 235f15b), acá
            en el micrositio público. */}
        {((hayPreOfertas && animal.precio_base != null) || ofertas?.length > 0) && (
          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col xs={24} lg={12}>
              {hayPreOfertas && animal.precio_base != null && (
                <PreOfertaCard
                  animal={animal}
                  evento={evento}
                  config={config}
                  tipografia={tipografia}
                  isAuthenticated={isAuthenticated}
                  usuarioId={user?.id_usuario}
                  mejorOferta={mejor_oferta}
                  onOfertaCreada={agregarOfertaAlHistorial}
                  onOfertandoChange={setOfertaEnVuelo}
                />
              )}
            </Col>
            <Col xs={24} lg={12}>
              <TablaOfertas ofertas={ofertas} config={config} tipografia={tipografia} />
            </Col>
          </Row>
        )}
      </div>

      {/* Floating prev/next — solo desktop */}
      <FloatingNav
        slug={slug}
        prevAnimal={prev_animal}
        nextAnimal={next_animal}
        config={config}
        tipografia={tipografia}
      />

      <SiteFooter
        config={config}
        tipografia={tipografia}
        evento={evento}
        categorias={categorias}
        logoUrl={logoUrl}
        onCategoriaSelect={(id) => navigate(`/remate/${slug}`, { state: { categoriaFiltro: id } })}
        slug={slug}
      />

      <style>{`
        .animal-floating-nav { display: block; }
        .animal-nav-row-prevnext { display: none; }
        @media (max-width: 991px) {
          .animal-floating-nav { display: none !important; }
          .animal-nav-row-prevnext {
            display: flex;
            justify-content: space-between;
            gap: 8px;
            margin-bottom: 10px;
          }
        }
        .animal-info-mobile { display: none; }
        @media (max-width: 991px) {
          .animal-info-mobile { display: block; }
        }
      `}</style>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// Fila de navegación: ← Catálogo (desktop + mobile) + prev/next (mobile)
// ═══════════════════════════════════════════════════════════════════
const NavRow = ({ slug, prevAnimal, nextAnimal, config, animal, evento }) => {
  const navigate = useNavigate();
  const colorPrimary = config.color_primary || '#1a5f2a';

  const handleCompartir = () => {
    // Usa la URL custom del productor si declaró embed externo en la config;
    // sino, window.location.href (default).
    const url = getShareUrl({
      config: evento?.configuracion_micrositio,
      animal,
      eventoSlug: evento?.slug,
    });
    const nomEvento = (evento?.nombre_evento || '').trim();
    const corralStr = animal.corral ? ` - ${terminoLote(config).mayus}: ${animal.corral}` : '';
    const texto = `Mirá este lote de ${nomEvento}:\n${(animal.nom_animal || '').trim()}${corralStr}\n\n${url}`;
    // Dominio FIJO, asi que no necesita lista blanca — pero el `noopener` si:
    // no depende de a donde se va, sino de que la pestaña nueva no pueda
    // tocar `window.opener` y redirigir ESTA pagina.
    window.open(`https://wa.me/?text=${encodeURIComponent(texto)}`, '_blank', 'noopener,noreferrer');
    // catch vacío a propósito, igual que en el track de visita: el envío a
    // WhatsApp ya se disparó. Un error de analytics no puede molestar a nadie.
    analyticsService.track('compartir_animal', evento.id_evento, {
      id_animal: animal.id_animal,
    }).catch(() => {});
  };

  const catBtnStyle = {
    background: colorPrimary,
    border: 'none',
    color: '#fff',
    borderRadius: 32,
    padding: '8px 16px',
    height: 'auto',
    lineHeight: 1,
    fontSize: 13,
    cursor: 'pointer',
  };

  const navBtnStyle = {
    background: 'rgba(0,0,0,0.07)',
    border: '1px solid rgba(0,0,0,0.1)',
    color: '#444',
    borderRadius: 32,
    padding: '7px 14px',
    height: 'auto',
    lineHeight: 1,
    fontSize: 13,
    cursor: 'pointer',
  };

  const navBtnDisabledStyle = {
    ...navBtnStyle,
    color: '#bbb',
    cursor: 'default',
    background: 'rgba(0,0,0,0.03)',
  };

  const whatsappBtn = (
    <Button
      style={{
        background: '#25D366',
        border: 'none',
        color: '#fff',
        borderRadius: 32,
        padding: '8px 16px',
        height: 'auto',
        lineHeight: 1,
        fontSize: 13,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
      }}
      icon={<WhatsAppOutlined />}
      onClick={handleCompartir}
    >
      Compartir
    </Button>
  );

  const prevBtn = prevAnimal ? (
    <Button
      style={navBtnStyle}
      onClick={() => navigate(`/remate/${slug}/animal/${buildAnimalSlug(prevAnimal)}`)}
    >
      <LeftOutlined style={{ fontSize: 11, marginRight: 4 }} />Prev.
    </Button>
  ) : (
    <Button style={navBtnDisabledStyle} disabled>
      <LeftOutlined style={{ fontSize: 11, marginRight: 4 }} />Prev.
    </Button>
  );

  const nextBtn = nextAnimal ? (
    <Button
      style={navBtnStyle}
      onClick={() => navigate(`/remate/${slug}/animal/${buildAnimalSlug(nextAnimal)}`)}
    >
      Sig.<RightOutlined style={{ fontSize: 11, marginLeft: 4 }} />
    </Button>
  ) : (
    <Button style={navBtnDisabledStyle} disabled>
      Sig.<RightOutlined style={{ fontSize: 11, marginLeft: 4 }} />
    </Button>
  );

  return (
    <div style={{ marginBottom: 7 }}>
      {/* Fila 1 — Prev (izq) / Sig (der) — solo mobile */}
      <div className="animal-nav-row-prevnext">
        {prevBtn}
        {nextBtn}
      </div>

      {/* Fila 2 — Catálogo (izq) / Compartir (der) */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Button
          style={catBtnStyle}
          onClick={() => navigate(`/remate/${slug}`)}
          icon={<ArrowLeftOutlined style={{ fontSize: 11 }} />}
        >
          Catálogo
        </Button>
        {whatsappBtn}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// Botones flotantes prev/next — solo desktop
// ═══════════════════════════════════════════════════════════════════
const FloatingNav = ({ slug, prevAnimal, nextAnimal, config, tipografia }) => {
  const colorPrimary = config.color_primary || '#1a5f2a';
  // El hook vive ACA y no adentro de `NavSideBtn` por dos motivos: un solo
  // listener en lugar de dos, y el re-render por scroll queda acotado a este
  // subarbol de tres nodos en vez de tocar `AnimalPage` entera.
  const centroVisible = useCentroVisibleDelPadre();
  return (
    <div className="animal-floating-nav">
      <NavSideBtn direction="prev" animal={prevAnimal} slug={slug} colorPrimary={colorPrimary} tipografia={tipografia} config={config} centroVisible={centroVisible} />
      <NavSideBtn direction="next" animal={nextAnimal} slug={slug} colorPrimary={colorPrimary} tipografia={tipografia} config={config} centroVisible={centroVisible} />
    </div>
  );
};

const NavSideBtn = ({ direction, animal, slug, colorPrimary, tipografia, config, centroVisible }) => {
  const [hover, setHover] = useState(false);
  const navigate = useNavigate();

  if (!animal) return null;

  const isLeft = direction === 'prev';

  // MISMO ASPECTO Y MISMO LUGAR. Lo unico que cambia es CONTRA QUE se posiciona.
  //
  // Standalone (`centroVisible` en null) se queda `fixed`, que es lo correcto y
  // lo que hubo siempre. Incrustado, `fixed` se fija al viewport DEL IFRAME
  // —que desde que el padre lo agranda mide lo mismo que todo el documento— y
  // la flecha se va con el scroll. Ahi pasa a `absolute` y se la clava en la
  // franja que el usuario realmente esta mirando, que el padre nos informa.
  // Ver `useCentroVisibleDelPadre`.
  //
  // `left`/`right: 0` con `absolute` resuelven contra el bloque contenedor
  // inicial —ningun ancestro esta posicionado— y el documento ocupa el 100% del
  // ancho del iframe: es el mismo borde de pantalla de siempre.
  const siguiendoAlPadre = centroVisible != null;

  return (
    <div
      style={{
        position: siguiendoAlPadre ? 'absolute' : 'fixed',
        top: siguiendoAlPadre ? centroVisible : '50%',
        [isLeft ? 'left' : 'right']: 0,
        transform: 'translateY(-50%)',
        zIndex: 200,
        display: 'flex',
        flexDirection: isLeft ? 'row' : 'row-reverse',
        alignItems: 'center',
        cursor: 'pointer',
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={() => navigate(`/remate/${slug}/animal/${buildAnimalSlug(animal)}`)}
    >
      {/* Flecha */}
      <div style={{
        background: colorPrimary,
        color: '#fff',
        width: 36,
        minHeight: 72,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        borderRadius: isLeft ? '0 8px 8px 0' : '8px 0 0 8px',
        fontSize: 16,
        boxShadow: isLeft ? '3px 0 10px rgba(0,0,0,0.2)' : '-3px 0 10px rgba(0,0,0,0.2)',
        transition: 'width 0.2s',
      }}>
        {isLeft ? <LeftOutlined /> : <RightOutlined />}
      </div>

      {/* Panel deslizable */}
      <div style={{
        maxWidth: hover ? 240 : 0,
        overflow: 'hidden',
        transition: 'max-width 0.25s ease',
        background: 'rgba(255,255,255,0.97)',
        backdropFilter: 'blur(8px)',
        borderRadius: isLeft ? '0 8px 8px 0' : '8px 0 0 8px',
        boxShadow: isLeft ? '4px 0 16px rgba(0,0,0,0.12)' : '-4px 0 16px rgba(0,0,0,0.12)',
      }}>
        <div style={{ padding: '12px 16px', display: 'flex', gap: 12, alignItems: 'flex-start', width: 240 }}>
          {imagenAnimalUrl(animal, config) ? (
            <img
              src={imagenAnimalUrl(animal, config)}
              alt={animal.nom_animal}
              style={{ width: 52, height: 52, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }}
            />
          ) : (
            <div style={{ width: 52, height: 52, background: '#f0f0f0', borderRadius: 6, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <PictureOutlined style={{ color: '#ccc' }} />
            </div>
          )}
          <div style={{ flex: 1, overflow: 'hidden' }}>
            <Text strong style={{
              display: 'block', fontSize: 13, color: colorPrimary,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              fontFamily: tipografia?.title,
            }}>
              {animal.nom_animal}
            </Text>
            <Divider style={{ margin: '5px 0' }} />
            {animal.corral && (
              <Text type="secondary" style={{ display: 'block', fontSize: 11 }}>
                {terminoLote(config).nombre} {animal.corral}
              </Text>
            )}
            {animal.nombre_categoria && (
              <Text type="secondary" style={{ display: 'block', fontSize: 11 }}>
                {animal.nombre_categoria}
              </Text>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// Hero con nombre + corral
// ═══════════════════════════════════════════════════════════════════
const AnimalHero = ({ animal, config, tipografia, heroTransparente, fondoHero }) => {
  const minHeight = config.hero_altura || (heroTransparente ? 300 : 210);
  const colorHeroText = config.color_hero_text || '#ffffff';
  const textTransform = config.text_transform_hero || 'none';
  const colorPrimary  = config.color_primary || '#1a5f2a';

  return (
    <div className="hero-zone" style={{
      position: 'relative',
      minHeight,
      display: 'flex', justifyContent: 'center',
      // Mismo helper que el resto del micrositio. Antes esta pagina pintaba el
      // fondo con un <img> + object-fit, y object-fit NO admite porcentajes:
      // con hero_bg_size='porcentaje' caia a 'cover' en silencio, asi que el
      // productor veia el 150% en la vista previa y otra cosa en la ficha.
      // Una sola ruta de codigo = un solo comportamiento.
      background: fondoHero
        ? heroBackground(config, fondoHero)
        : colorPrimary,
      overflow: 'hidden',
      ...heroTextLayout(config),
    }}>
      <div style={{
        position: 'relative', textAlign: 'center',
        paddingLeft: 32, paddingRight: 32,
        ...heroTextPadding(config, 24),
      }}>
        {animal.nombre_categoria && (
          <Tag style={{ marginBottom: 12, background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', fontFamily: tipografia.body }}>
            {animal.nombre_categoria}
          </Tag>
        )}
        <Title
          level={1}
          style={{
            color: colorHeroText, fontFamily: tipografia.title,
            textTransform, margin: 0, fontSize: config.hero_text_size || 32,
          }}
        >
          {animal.corral ? `${terminoLote(config).mayus}: ${animal.corral}` : animal.nom_animal}
        </Title>
        {/* Corral y premio movidos a la fila de tags principales del card del
            animal (junto a categoria + raza + co-propietaria). El premio ya
            figura en la info principal del animal mas abajo, asi se evita
            la duplicacion en la hero zone. */}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// Carrusel: video → foto principal → fotos familiares → vacío
// Navegación por miniaturas. Caption dinámico por tipo de slide.
// ═══════════════════════════════════════════════════════════════════
const SLIDE_H = 380;
const THUMB_W = 88;
const THUMB_H = 62;

const MEDIA_BOX = {
  height: SLIDE_H,
  background: '#111',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
};

// Se EXPORTA para que la ficha del catalogo de genetica lo use tal cual: es el
// mismo orden de medios —video, foto principal, fotos de familiares— y la misma
// navegacion por miniaturas. Copiarlo seria mantener dos carruseles.
export const CarruselMedia = ({
  imagenes, familiares, videoUrl, config, premio, mostrarCopa,
  // Opcional: le avisa al padre QUE FOTO se esta viendo, como indice dentro de
  // `[...imagenes, ...familiares CON foto]` — o `null` si el slide actual es el
  // VIDEO, que no es una foto y no se abre en un visor.
  //
  // El indice del CARRUSEL no sirve para eso: sus slides son video + fotos +
  // familiares, y el video corre todo un lugar. El que viaja es el de las
  // FOTOS, que es el orden en que hay que armar los `items` del visor.
  //
  // ⚠️ Los familiares SIN foto no cuentan, porque el carrusel no les da slide.
  // Quien arme el visor tiene que filtrarlos IGUAL o los indices se corren.
  //
  // Sin esta prop el componente se comporta igual que siempre.
  onCambioFoto,
}) => {
  const colorPrimary = config.color_primary || '#1a5f2a';
  const carouselRef  = useRef(null);
  const [current, setCurrent]  = useState(0);

  // Video ID para embed + thumbnail de YouTube
  const videoIdMatch = videoUrl?.match(/(?:v=|youtu\.be\/)([^&?/]+)/);
  const videoId  = videoIdMatch?.[1] ?? null;
  const embedUrl = videoId ? `https://www.youtube.com/embed/${videoId}` : null;

  const famConFoto = (familiares || []).filter(f => f.url_foto_familiar);

  // ── Construir slides como array limpio ──────────────────────────
  const slides = [
    ...(embedUrl ? [{
      key: 'video',
      content: (
        <div style={MEDIA_BOX}>
          <iframe
            src={embedUrl}
            title="Video del animal"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            style={{ width: '100%', height: SLIDE_H, border: 0 }}
          />
        </div>
      ),
      thumb: (
        <div style={{ width: '100%', height: '100%', position: 'relative', background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <img
            src={`https://img.youtube.com/vi/${videoId}/mqdefault.jpg`}
            alt="video"
            style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.7 }}
          />
          <PlayCircleOutlined style={{ position: 'absolute', color: '#fff', fontSize: 24, filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.8))' }} />
        </div>
      ),
      caption: null,
    }] : []),

    ...(imagenes || []).map((img, i) => ({
      key: `img-${i}`,
      content: (
        <div style={{ ...MEDIA_BOX, background: '#f5f5f5' }}>
          <img
            src={fixUrl(img.url_web || img.url_imagen)}
            alt={`Imagen ${i + 1}`}
            style={{ maxWidth: '100%', maxHeight: SLIDE_H, objectFit: 'contain' }}
          />
        </div>
      ),
      thumb: (
        <img
          src={fixUrl(img.url_thumb || img.url_imagen)}
          alt={`miniatura ${i + 1}`}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ),
      caption: premio ? (
        <Tag color="gold" style={{ fontSize: 13 }}>
          {mostrarCopa !== 0 && <TrophyOutlined style={{ marginRight: 4 }} />}{premio}
        </Tag>
      ) : null,
    })),

    ...famConFoto.map((f, i) => ({
      key: `fam-${i}`,
      content: (
        <div style={{ ...MEDIA_BOX, background: '#f5f5f5' }}>
          <img
            src={fixUrl(f.url_foto_familiar)}
            alt={f.nom_familiar}
            style={{ maxWidth: '100%', maxHeight: SLIDE_H, objectFit: 'contain' }}
          />
        </div>
      ),
      thumb: (
        <img
          src={fixUrl(f.url_foto_familiar)}
          alt={f.nom_familiar}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ),
      caption: (
        <div>
          <Text strong style={{ fontSize: 14 }}>{f.nom_familiar}</Text>
          {f.vinculo_familiar && (
            <Text type="secondary" style={{ fontSize: 12, marginLeft: 6 }}>— {f.vinculo_familiar}</Text>
          )}
          {f.premio_familiar && (
            <div style={{ marginTop: 4 }}>
              <Tag color="gold" style={{ fontSize: 12 }}>
                {f.mostrar_copa !== 0 && <TrophyOutlined style={{ marginRight: 4 }} />}{f.premio_familiar}
              </Tag>
            </div>
          )}
        </div>
      ),
    })),
  ];

  if (slides.length === 0) {
    return (
      <Card style={{ marginBottom: 24, textAlign: 'center', color: '#bbb' }} styles={{ body: { padding: 40 } }}>
        <PictureOutlined style={{ fontSize: 48, marginBottom: 8 }} />
        <div>Sin imágenes cargadas</div>
      </Card>
    );
  }

  // Deriva la foto del slide por su `key` (`video`, `img-N`, `fam-N`) y avisa.
  // Se llama en los DOS puntos donde cambia el slide —las miniaturas y el
  // propio carrusel— en vez de en un `useEffect` sobre `current`: `slides` se
  // recrea en cada render, asi que como dependencia haria un loop.
  // QUE INDICE SE REPORTA, y esto es un contrato con quien abra un visor.
  //
  // El carrusel muestra TRES clases de slide —el video, las fotos propias y las
  // de los familiares— pero el visor solo puede abrir FOTOS. Asi que el indice
  // que viaja es la posicion dentro de `[...imagenes, ...familiares]`, que es
  // exactamente el orden en que hay que armar los `items` del visor.
  //
  // Antes solo reportaba los `img-N` y devolvia `null` para todo lo demas. Con
  // animales de UNA sola foto propia y cuatro de familiares —que es el caso de
  // los nueve publicados hoy— eso dejaba el indice clavado en 0: el comprador
  // miraba la foto del padre, tocaba "Ver en grande" y le aparecia otra, sin
  // flechas para moverse porque el visor tenia un solo item.
  //
  // El video sigue devolviendo `null`: no es una foto y no entra al visor.
  const avisarFoto = (i) => {
    if (!onCambioFoto) return;
    const clave = slides[i]?.key || '';
    const esImagen = clave.match(/^img-(\d+)$/);
    if (esImagen) return onCambioFoto(Number(esImagen[1]));
    const esFamiliar = clave.match(/^fam-(\d+)$/);
    if (esFamiliar) return onCambioFoto(imagenes.length + Number(esFamiliar[1]));
    return onCambioFoto(null);
  };

  const goTo = (i) => {
    setCurrent(i);
    avisarFoto(i);
    carouselRef.current?.goTo(i);
  };

  const currentCaption = slides[current]?.caption;
  const hasMultiple    = slides.length > 1;

  return (
    <Card style={{ marginBottom: 24, borderRadius: 8, overflow: 'hidden' }} styles={{ body: { padding: 0 } }}>

      {/* Slide principal */}
      {/* `beforeChange` y NO `afterChange`: antd fuerza `waitForAnimate = false`,
          y si se pasa de slide antes de que termine la animacion —500ms—
          `@ant-design/react-slick` cancela el timeout que iba a reportar el
          indice nuevo y dispara `afterChange` con el ANTERIOR. Aca eso desfasa
          el epigrafe: se lee el pie de la foto que ya se paso. Es el mismo bug
          que tenian las flechas del carrusel de la portada del catalogo. */}
      <Carousel
        ref={carouselRef}
        dots={false}
        beforeChange={(_desde, hacia) => { setCurrent(hacia); avisarFoto(hacia); }}
      >
        {slides.map(slide => (
          <div key={slide.key}>{slide.content}</div>
        ))}
      </Carousel>

      {/* Caption dinámico — solo si hay contenido */}
      {currentCaption && (
        <div style={{
          padding: '10px 16px',
          borderTop: '1px solid #f0f0f0',
          textAlign: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 44,
          background: '#fff',
        }}>
          {currentCaption}
        </div>
      )}

      {/* Tira de miniaturas */}
      {hasMultiple && (
        <div style={{
          display: 'flex',
          gap: 6,
          padding: '8px 10px',
          overflowX: 'auto',
          background: '#f2f2f2',
          borderTop: '1px solid #e8e8e8',
        }}>
          {slides.map((slide, i) => (
            <div
              key={slide.key}
              onClick={() => goTo(i)}
              style={{
                width: THUMB_W,
                height: THUMB_H,
                flexShrink: 0,
                cursor: 'pointer',
                border: `2px solid ${i === current ? colorPrimary : '#ddd'}`,
                borderRadius: 4,
                overflow: 'hidden',
                transition: 'border-color 0.15s',
                background: '#ccc',
              }}
            >
              {slide.thumb}
            </div>
          ))}
        </div>
      )}

    </Card>
  );
};

// ═══════════════════════════════════════════════════════════════════
// Info de nacimiento — nombre, categoría, raza + datos de nacimiento
// ═══════════════════════════════════════════════════════════════════
const InfoNacimientoCard = ({ animal, config, tipografia }) => {
  const colorPrimary = config.color_primary || '#1a5f2a';

  const fila = (label, value) =>
    value != null && value !== '' ? (
      <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #f5f5f5' }}>
        <Text type="secondary" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</Text>
        <Text strong style={{ fontSize: 13 }}>{value}</Text>
      </div>
    ) : null;

  const peloCabeza = animal.pelo || animal.cabeza;
  const labelPeloCabeza = animal.pelo ? 'Pelo' : 'Cabeza';

  return (
    <Card style={{ marginBottom: 16 }} styles={{ body: { padding: '20px 24px' } }}>
      <div style={{ marginBottom: 12 }}>
        <Title level={3} style={{ margin: 0, fontFamily: tipografia.title, color: colorPrimary, lineHeight: 1.2 }}>
          {animal.nom_animal}
        </Title>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
          {animal.corral && (
            <Tag
              style={{
                margin: 0,
                background: colorPrimary,
                color: '#fff',
                border: 'none',
                fontWeight: 600,
              }}
            >
              {animal.agrupado
                ? `${terminoLote(config).nombre} Agrupado ${animal.corral}${animal.cantidad_corral ? ` — ${animal.cantidad_corral}` : ''}`
                : `${terminoLote(config).nombre} ${animal.corral}`}
            </Tag>
          )}
          {animal.es_premium === 1 && (
            <Tag style={{ margin: 0, background: colorPrimary, color: '#fff', border: 'none', fontWeight: 600 }}>
              Premium <StarFilled style={{ fontSize: 11 }} />
            </Tag>
          )}
          {animal.categoria?.nom_categoria && (
            <Tag color={colorPrimary} style={{ margin: 0 }}>{animal.categoria.nom_categoria}</Tag>
          )}
          {animal.nom_raza && (
            <Tag style={{ margin: 0 }}>{animal.nom_raza}</Tag>
          )}
          {/* De qué EMPRESA viene el animal, en los remates de varias cabañas.
              `animal.nom_cabana` — NO `animal.cabana.nom_cabana`. Ese segundo
              es el nombre de la UNIDAD interna (ej. "Cabaña Pilagá Quimilí");
              públicamente siempre se muestra la marca de la empresa dueña
              ("Cabaña Pilagá"), resuelta por el backend en
              `Animal.resolver_procedencia()`. Sin eso asignado, la etiqueta
              no se dibuja. */}
          {animal.nom_cabana && (
            <Tag color="blue" style={{ margin: 0 }}>{animal.nom_cabana}</Tag>
          )}
          {animal.co_propietario && (
            <Tag icon={<span style={{ marginRight: 4 }}>🤝</span>} style={{ margin: 0 }}>Co-Propietario: {animal.co_propietario}</Tag>
          )}
        </div>
      </div>

      {animal.mensaje?.trim() && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10 }}>
          <div style={{
            background: `${colorPrimary}18`,
            border: `1px solid ${colorPrimary}40`,
            borderRadius: 6,
            padding: '7px 12px',
            color: colorPrimary,
            fontWeight: 600,
            fontSize: 13,
          }}>
            📢 {animal.mensaje}
          </div>
        </div>
      )}

      <Divider style={{ margin: '12px 0' }} />
      {fila('RP', animal.rp_animal)}
      {fila('Fecha de nacimiento', animal.fecha_nac && fmtFecha(animal.fecha_nac))}
      {fila('Peso al nacer', animal.peso_nacer ? `${animal.peso_nacer} kg` : null)}
      {fila('Registro', animal.registro)}
      {peloCabeza ? fila(labelPeloCabeza, peloCabeza) : null}
    </Card>
  );
};

// ═══════════════════════════════════════════════════════════════════
// Información actual — medidas, identificación, caravana color swatch
// ═══════════════════════════════════════════════════════════════════
const InformacionActualCard = ({ animal, tipo, config, tipografia }) => {
  const colorPrimary = config.color_primary || '#1a5f2a';

  const fila = (label, value) =>
    value != null && value !== '' ? (
      <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #f5f5f5' }}>
        <Text type="secondary" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</Text>
        <Text strong style={{ fontSize: 13 }}>{value}</Text>
      </div>
    ) : null;

  const labelCaravana = animal.agrupado ? 'Caravana / Pintura' : 'Caravana';

  const tieneContenido = animal.adn_animal || animal.hba_animal || animal.aab || animal.aba ||
    animal.caravana || animal.peso_actual || animal.ce_actual || animal.aob_actual ||
    animal.egd_actual || animal.gi_actual || animal.frame;

  if (!tieneContenido) return null;

  return (
    <Card
      style={{ marginBottom: 16 }}
      styles={{ body: { padding: '20px 24px' } }}
      title={<span style={{ color: colorPrimary, fontFamily: tipografia.title, fontSize: 14 }}>Información Actual</span>}
    >
      {fila('ADN', animal.adn_animal)}
      {fila('HBA', animal.hba_animal)}
      {animal.aab != null && fila('AAB', animal.aab)}
      {animal.aba != null && fila('ABA', animal.aba)}

      {animal.caravana && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #f5f5f5' }}>
          <Text type="secondary" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.4 }}>{labelCaravana}</Text>
          <div style={{
            width: 22, height: 22, borderRadius: 4,
            backgroundColor: colorCaravana(animal.caravana),
            border: '1px solid #d9d9d9',
            flexShrink: 0,
          }} />
        </div>
      )}

      {fila('Peso actual', animal.peso_actual ? `${animal.peso_actual} kg` : null)}
      {tipo === VIS_TORO && fila('Circ. Escrotal', animal.ce_actual ? `${animal.ce_actual} cm` : null)}
      {fila('AOB', animal.aob_actual)}
      {fila('EGD', animal.egd_actual)}
      {fila('%GI', animal.gi_actual)}
      {fila('Frame', animal.frame)}
    </Card>
  );
};

// ═══════════════════════════════════════════════════════════════════
// Card principal de vis 2 AGRUPADA: tabla con los datos de nacimiento de
// TODOS los animales del corral (RP · F.NAC · P.N · REG · CAB · ABA/HBA),
// paginada de a 8 con flechas. El corral se vende completo, por eso se
// muestran todos sin importar cuál esté viendo el visitante. El RP es un tag
// con el color de la caravana, clickeable a la ficha del animal.
// ═══════════════════════════════════════════════════════════════════
const PER_PAGINA_CORRAL = 8;
const CardCorralAgrupado = ({ animal, companeros, slug, config, tipografia }) => {
  const colorPrimary = config.color_primary || '#1a5f2a';
  const [pagina, setPagina] = useState(0);

  const todos = [animal, ...(companeros || [])];
  const totalPag = Math.ceil(todos.length / PER_PAGINA_CORRAL);
  const visibles = todos.slice(pagina * PER_PAGINA_CORRAL, pagina * PER_PAGINA_CORRAL + PER_PAGINA_CORRAL);

  const thSt = { textAlign: 'left', padding: '6px 8px', fontSize: 12, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 700, whiteSpace: 'nowrap' };
  const tdSt = { padding: '6px 8px', fontSize: 13, borderTop: '1px solid #f0f0f0', whiteSpace: 'nowrap' };
  // ABA o HBA, el que tenga cargado (son excluyentes).
  const regGen = (a) => a.aba || a.hba_animal || '—';

  return (
    <Card style={{ marginBottom: 16 }} styles={{ body: { padding: '20px 24px' } }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 12 }}>
        <div style={{ minWidth: 0 }}>
          <Title level={3} style={{ margin: 0, fontFamily: tipografia.title, color: colorPrimary, lineHeight: 1.2 }}>
            {animal.categoria?.nom_categoria || animal.nom_animal}
          </Title>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
            {animal.corral && (
              <Tag style={{ margin: 0, background: colorPrimary, color: '#fff', border: 'none', fontWeight: 600 }}>
                {terminoLote(config).nombre} Agrupado {animal.corral}{animal.cantidad_corral ? ` — ${animal.cantidad_corral} cabezas` : ''}
              </Tag>
            )}
            {animal.nom_raza && <Tag style={{ margin: 0 }}>{animal.nom_raza}</Tag>}
          </div>
        </div>
        {totalPag > 1 && (
          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            <Button size="small" icon={<LeftOutlined />} disabled={pagina === 0} onClick={() => setPagina(p => Math.max(0, p - 1))} />
            <Button size="small" icon={<RightOutlined />} disabled={pagina >= totalPag - 1} onClick={() => setPagina(p => Math.min(totalPag - 1, p + 1))} />
          </div>
        )}
      </div>

      <Text strong style={{ display: 'block', fontSize: 13, color: colorPrimary, fontFamily: tipografia.title, marginBottom: 4 }}>
        Animales que componen este {terminoLote(config).minus}:
      </Text>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: tipografia.body }}>
          <thead>
            <tr>
              <th style={thSt}>RP</th>
              <th style={thSt}>F.NAC</th>
              <th style={thSt}>P.N</th>
              <th style={thSt}>REG.</th>
              <th style={thSt}>CAB.</th>
              <th style={thSt}>ABA</th>
            </tr>
          </thead>
          <tbody>
            {visibles.map(a => (
              <tr key={a.id_animal}>
                <td style={tdSt}>
                  <Link to={`/remate/${slug}/animal/${buildAnimalSlug(a)}`}>
                    <Tag style={{
                      margin: 0,
                      cursor: 'pointer',
                      borderColor: 'transparent',
                      background: colorCaravana(a.caravana) || 'inherit',
                      color: '#1a1a1a',
                    }}>
                      RP {a.rp_animal}
                    </Tag>
                  </Link>
                </td>
                <td style={tdSt}>{fmtFecha(a.fecha_nac)}</td>
                <td style={tdSt}>{a.peso_nacer ?? '—'}</td>
                <td style={tdSt}>{a.registro || '—'}</td>
                <td style={tdSt}>{a.cabeza || '—'}</td>
                <td style={tdSt}>{regGen(a)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

// ═══════════════════════════════════════════════════════════════════
// Tabla de servicio del lote — agrupado=1, tipos 2 y 5
// Desktop: tabla full-width | Mobile: scroll horizontal de tarjetas
// ═══════════════════════════════════════════════════════════════════
const TablaServicioCorral = ({ animal, companeros, slug, config, tipografia }) => {
  const colorPrimary = config.color_primary || '#1a5f2a';

  const todos = [
    { ...animal, es_actual: true },
    ...(companeros || []).map(c => ({ ...c, es_actual: false })),
  ].filter(a => a.servicio?.tipo || a.servicio?.toro || a.servicio?.estado);

  if (todos.length === 0) return null;

  const estadoTag = (estado) => {
    if (!estado) return null;
    const e = estado.toLowerCase();
    const color = e.includes('confirmada') ? 'success'
      : e.includes('dudosa') ? 'warning'
      : (e.includes('vacía') || e.includes('vacia')) ? 'error'
      : 'default';
    return <Tag color={color} style={{ margin: 0 }}>{estado}</Tag>;
  };

  const filaCard = (label, value) => value ? (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '3px 0', borderBottom: '1px solid #f5f5f5' }}>
      <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.3, flexShrink: 0 }}>{label}</Text>
      <Text style={{ fontSize: 12, fontWeight: 500, textAlign: 'right' }}>{value}</Text>
    </div>
  ) : null;

  const columns = [
    {
      title: '',
      key: 'link',
      width: 36,
      render: (_, r) => r.es_actual
        ? <Text type="secondary" style={{ fontSize: 12 }}>●</Text>
        : (
          <Link to={`/remate/${slug}/animal/${buildAnimalSlug(r)}`} title={`Ver ficha de ${r.nom_animal}`}>
            <SearchOutlined style={{ color: colorPrimary, fontSize: 15 }} />
          </Link>
        ),
    },
    { title: 'RP',          dataIndex: 'rp_animal',  key: 'rp',    width: 90 },
    { title: 'Nombre',      dataIndex: 'nom_animal', key: 'nombre' },
    { title: 'Tipo',        key: 'tipo',   width: 60,  render: (_, r) => r.servicio?.tipo  || '—' },
    { title: 'Toro',        key: 'toro',             render: (_, r) => r.servicio?.toro  || '—' },
    // `fecha_servicio` es texto libre (un período), no una fecha: va tal cual.
    { title: 'F. Servicio', key: 'fserv',  width: 105, render: (_, r) => r.servicio?.fecha_servicio || '—' },
    { title: 'Parto est.',  key: 'parto',  width: 95,  render: (_, r) => fmtFecha(r.servicio?.fecha_parto)    || '—' },
    { title: 'Estado',      key: 'estado',            render: (_, r) => estadoTag(r.servicio?.estado) },
  ];

  const titulo = (
    <span style={{ color: colorPrimary, fontFamily: tipografia.title, fontSize: 14 }}>
      Servicio del Lote ({todos.length} hembras)
    </span>
  );

  return (
    <Card style={{ marginBottom: 16 }} styles={{ body: { padding: '16px 20px' } }} title={titulo}>

      {/* ── Desktop: tabla ── */}
      <div className="servicio-lote-table">
        <Table
          dataSource={todos}
          columns={columns}
          rowKey="id_animal"
          size="small"
          pagination={false}
          onRow={(record) => ({
            style: record.es_actual ? { background: `${colorPrimary}0d`, fontWeight: 600 } : {},
          })}
        />
      </div>

      {/* ── Mobile: scroll horizontal de tarjetas ── */}
      <div className="servicio-lote-scroll">
        {todos.map(r => (
          <div
            key={r.id_animal}
            className="servicio-lote-card"
            style={{
              border: `2px solid ${r.es_actual ? colorPrimary : '#e8e8e8'}`,
              background: r.es_actual ? `${colorPrimary}06` : 'white',
            }}
          >
            {/* Cabecera de la tarjeta */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 13, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.nom_animal}
                </div>
                <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>RP {r.rp_animal}</div>
              </div>
              <div style={{ marginLeft: 8, flexShrink: 0 }}>
                {r.es_actual
                  ? <Tag color="blue" style={{ fontSize: 10, margin: 0 }}>Esta ficha</Tag>
                  : (
                    <Link to={`/remate/${slug}/animal/${buildAnimalSlug(r)}`}>
                      <SearchOutlined style={{ color: colorPrimary, fontSize: 18 }} />
                    </Link>
                  )
                }
              </div>
            </div>

            {/* Datos de servicio */}
            {filaCard('Tipo', r.servicio?.tipo)}
            {filaCard('Toro', r.servicio?.toro)}
            {filaCard('F. Servicio', r.servicio?.fecha_servicio)}
            {filaCard('Parto est.', r.servicio?.fecha_parto && fmtFecha(r.servicio.fecha_parto))}

            {/* Estado */}
            {r.servicio?.estado && (
              <div style={{ marginTop: 10 }}>
                {estadoTag(r.servicio.estado)}
              </div>
            )}
          </div>
        ))}
      </div>

    </Card>
  );
};

// ═══════════════════════════════════════════════════════════════════
// Tabla integrada del lote (vis 2): genealogía + servicio + genética.
// Desktop: una tabla con 3 grupos de columnas, alineada por RP.
// Mobile: una tarjeta por animal con las 3 secciones apiladas.
// Misma estética que TablaServicioCorral.
// ═══════════════════════════════════════════════════════════════════
const TablaLoteIntegrada = ({ animal, companeros, slug, config, tipografia }) => {
  const colorPrimary = config.color_primary || '#1a5f2a';
  const [modalVisible, setModalVisible] = useState(false);
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md; // en mobile hay cards, no la tabla

  const todos = [
    { ...animal, es_actual: true },
    ...(companeros || []).map(c => ({ ...c, es_actual: false })),
  ];
  if (todos.length === 0) return null;

  // Mismo filtro de columnas vacías que TablaGenetica, pero sobre TODO el
  // lote: una columna se oculta solo si NINGÚN animal de la tabla tiene dato
  // en ese DEP (deps/precisión/ranking los tres en null). Acá NO se aplica
  // el cambio de vista (tabla/gráfico): esta tabla integrada es un formato
  // propio de la vis 2 agrupada, no pasa por config.deps_vista.
  const colsConDatos = COLS_GENETICA.filter(col =>
    todos.some(r => r.deps?.[col.key] != null || r.precision?.[col.key] != null || r.ranking?.[col.key] != null)
  );

  const estadoTag = (estado) => {
    if (!estado) return null;
    const e = estado.toLowerCase();
    const color = e.includes('confirmada') ? 'success'
      : e.includes('dudosa') ? 'warning'
      : (e.includes('vacía') || e.includes('vacia')) ? 'error'
      : 'default';
    return <Tag color={color} style={{ margin: 0 }}>{estado}</Tag>;
  };

  const madre = (r) => r.genealogia?.padres?.madre || '—';
  const padre = (r) => r.genealogia?.padres?.padre || '—';

  // Tipo de servicio abreviado: Servicio Natural → S.N, I.A. → I.A.
  const fmtTipo = (tipo) => {
    if (!tipo) return '—';
    const t = tipo.toLowerCase();
    if (t.includes('natural')) return 'S.N';
    if (t.includes('i.a') || t.includes('insem')) return 'I.A.';
    return tipo;
  };

  // Celda genética: DEP / Precisión / Ranking apilados (compacto).
  const celdaGen = (r, key) => (
    <div style={{ lineHeight: 1.35 }}>
      <div style={{ fontWeight: 600, fontSize: 12 }}>{r.deps?.[key] ?? '—'}</div>
      <div style={{ fontSize: 11, color: '#8c8c8c' }}>{r.precision?.[key] ?? '—'}</div>
      <div style={{ fontSize: 11, color: colorPrimary, fontWeight: 500 }}>{r.ranking?.[key] ?? '—'}</div>
    </div>
  );

  const columns = [
    {
      title: '', key: 'link', width: 34, fixed: 'left',
      render: (_, r) => r.es_actual
        ? <Text type="secondary" style={{ fontSize: 12 }}>●</Text>
        : (
          <Link to={`/remate/${slug}/animal/${buildAnimalSlug(r)}`} title={`Ver ficha de ${r.nom_animal}`}>
            <SearchOutlined style={{ color: colorPrimary, fontSize: 15 }} />
          </Link>
        ),
    },
    { title: 'RP', dataIndex: 'rp_animal', key: 'rp', width: 80, fixed: 'left' },
    {
      title: 'GENEALOGÍA', className: 'grp-sep',
      children: [
        { title: 'Madre', key: 'madre', className: 'grp-sep', render: (_, r) => madre(r) },
        { title: 'Padre', key: 'padre', render: (_, r) => padre(r) },
      ],
    },
    {
      title: 'SERVICIO', className: 'grp-sep',
      children: [
        { title: 'Tipo',        key: 'tipo',  width: 54,  className: 'grp-sep', render: (_, r) => fmtTipo(r.servicio?.tipo) },
        { title: 'Toro',        key: 'toro',              render: (_, r) => r.servicio?.toro || '—' },
        { title: 'F. Servicio', key: 'fserv', width: 100, render: (_, r) => r.servicio?.fecha_servicio || '—' },
        { title: 'Parto est.',  key: 'parto', width: 95,  render: (_, r) => fmtFecha(r.servicio?.fecha_parto) },
        { title: 'Reg',         key: 'estado',            render: (_, r) => estadoTag(r.servicio?.estado) },
      ],
    },
    // El grupo entero se omite si NINGÚN animal del lote tiene un DEP: un
    // grupo con cabecera y cero columnas abajo es un título sin sentido.
    ...(colsConDatos.length > 0 ? [{
      title: "DEP's / PRECISIÓN / RANKING", className: 'grp-sep',
      children: colsConDatos.map((col, i) => ({
        title: col.label, key: `gen_${col.key}`, align: 'center', width: 56,
        className: i === 0 ? 'grp-sep' : undefined,
        render: (_, r) => celdaGen(r, col.key),
      })),
    }] : []),
  ];

  const tablaEl = (
    <Table
      className="lote-integrada-table"
      dataSource={todos}
      columns={columns}
      rowKey="id_animal"
      size="small"
      pagination={false}
      scroll={{ x: 'max-content' }}
      onRow={(record) => ({
        style: record.es_actual ? { background: `${colorPrimary}0d`, fontWeight: 600 } : {},
      })}
    />
  );

  const titulo = (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
      <span style={{ color: colorPrimary, fontFamily: tipografia.title, fontSize: 14 }}>
        Datos del Lote ({todos.length} animales)
      </span>
      {!isMobile && (
        <Button
          type="link"
          size="small"
          icon={<FullscreenOutlined />}
          onClick={() => setModalVisible(true)}
          style={{ color: colorPrimary, padding: 0, fontWeight: 600 }}
        >
          Ver tabla completa
        </Button>
      )}
    </div>
  );

  const filaCard = (label, value) => value ? (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '3px 0', borderBottom: '1px solid #f5f5f5' }}>
      <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.3, flexShrink: 0 }}>{label}</Text>
      <Text style={{ fontSize: 12, fontWeight: 500, textAlign: 'right' }}>{value}</Text>
    </div>
  ) : null;

  const seccionTitulo = (txt) => (
    <div style={{ fontSize: 11, fontWeight: 700, color: colorPrimary, textTransform: 'uppercase', letterSpacing: 0.4, margin: '10px 0 4px' }}>{txt}</div>
  );

  return (
    <>
    <Card style={{ marginBottom: 16 }} styles={{ body: { padding: 0 } }} title={titulo}>
      {/* ── Desktop: tabla integrada con 3 grupos de columnas ── */}
      <style>{`
        .lote-integrada-table .ant-table-cell {
          padding: 6px 6px !important;
        }
        .lote-integrada-table .ant-table-tbody .grp-sep {
          border-left: 2px solid #d9d9d9 !important;
        }
      `}</style>
      <div className="servicio-lote-table">
        {tablaEl}
      </div>

      {/* ── Mobile: una tarjeta por animal, las 3 secciones apiladas ── */}
      <div className="servicio-lote-scroll">
        {todos.map(r => (
          <div
            key={r.id_animal}
            className="servicio-lote-card"
            style={{
              border: `2px solid ${r.es_actual ? colorPrimary : '#e8e8e8'}`,
              background: r.es_actual ? `${colorPrimary}06` : 'white',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
              <div style={{ fontWeight: 700, fontSize: 13 }}>RP {r.rp_animal || '—'}</div>
              <div style={{ marginLeft: 8, flexShrink: 0 }}>
                {r.es_actual
                  ? <Tag color="blue" style={{ fontSize: 10, margin: 0 }}>Esta ficha</Tag>
                  : (
                    <Link to={`/remate/${slug}/animal/${buildAnimalSlug(r)}`}>
                      <SearchOutlined style={{ color: colorPrimary, fontSize: 18 }} />
                    </Link>
                  )
                }
              </div>
            </div>

            {seccionTitulo('Genealogía')}
            {filaCard('Madre', madre(r))}
            {filaCard('Padre', padre(r))}

            {seccionTitulo('Servicio')}
            {filaCard('Tipo', r.servicio?.tipo && fmtTipo(r.servicio.tipo))}
            {filaCard('Toro', r.servicio?.toro)}
            {filaCard('F. Servicio', r.servicio?.fecha_servicio)}
            {filaCard('Parto est.', r.servicio?.fecha_parto && fmtFecha(r.servicio.fecha_parto))}
            {r.servicio?.estado && <div style={{ marginTop: 6 }}>{estadoTag(r.servicio.estado)}</div>}

            {colsConDatos.length > 0 && (
              <>
                {seccionTitulo('Genética · DEP / Prec. / Rank.')}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 6 }}>
                  {colsConDatos.map(col => (
                    <div key={col.key} style={{ textAlign: 'center', background: '#fafafa', borderRadius: 4, padding: '4px 2px' }}>
                      <div style={{ fontSize: 10, color: '#888', fontWeight: 600 }}>{col.label}</div>
                      <div style={{ fontSize: 11, fontWeight: 600 }}>{r.deps?.[col.key] ?? '—'}</div>
                      <div style={{ fontSize: 10, color: '#8c8c8c' }}>{r.precision?.[col.key] ?? '—'}</div>
                      <div style={{ fontSize: 10, color: colorPrimary }}>{r.ranking?.[col.key] ?? '—'}</div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </Card>

      <Modal
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        title={<span style={{ color: colorPrimary, fontFamily: tipografia.title }}>Datos del Lote ({todos.length} animales)</span>}
        width="95vw"
        style={{ top: 24 }}
        styles={{ body: { padding: 16, maxHeight: 'calc(100vh - 140px)', overflow: 'auto' } }}
      >
        {tablaEl}
      </Modal>
    </>
  );
};

// ═══════════════════════════════════════════════════════════════════
// Crías al pie — solo para tipo 3 (hembra c/cría) y tipo 5
// ═══════════════════════════════════════════════════════════════════
// Desde el 2026-10-21 una madre puede tener VARIAS (las ELECCIONES: el que gana
// la puja elige un ternero). Con una sola, la card de siempre. Con varias, una
// tabla con una fila por ternero, y de columnas SOLO las que algún ternero
// tiene cargadas — la misma regla que los DEP's: en una elección a veces sólo
// se sabe el RP y el sexo, y siete columnas vacías serían ruido.
// El nacimiento va TAL CUAL lo escribió la cabaña («2026», «12/08/2026»), sin
// pasar por `fmtFecha`: es texto libre, como la fecha de servicio.
//
// La tabla vive en la columna angosta de la ficha (~440px útiles en un
// escritorio de 1440) y con los rótulos largos pedía 669px: el padre salía
// cortado y peso y pelo quedaban detrás de un scroll horizontal. Por eso va
// COMPACTA (usuario, 2026-10-21): sexo como «♂ M» / «♀ H», «Nac.» (el año solo
// es el uso óptimo; una fecha exacta ensancha la columna), «PN» por peso al
// nacer, pelo en su inicial (C / N), y el padre es la única columna que parte
// renglón. La card de UNA sola cría sigue con los rótulos enteros: ahí sobra
// lugar.
const SEXO_CRIA = {
  M: { label: '♂ Macho', corto: '♂ M', bg: '#e6f4ff', border: '#91caff', text: '#0958d9' },
  H: { label: '♀ Hembra', corto: '♀ H', bg: '#fff0f6', border: '#ffadd2', text: '#c41d7f' },
};

const BadgeSexoCria = ({ sexo, corto = false, movil = false }) => {
  const s = SEXO_CRIA[sexo];
  if (!s) return null;
  return (
    <span style={{
      display: 'inline-block', background: s.bg, border: `1px solid ${s.border}`,
      color: s.text, borderRadius: 6, fontWeight: 700, whiteSpace: 'nowrap',
      padding: corto ? (movil ? '0 5px' : '1px 7px') : '3px 12px',
      fontSize: corto ? (movil ? 11 : 12) : 13,
    }}>
      {corto ? s.corto : s.label}
    </span>
  );
};

const CELDA_MOVIL = { padding: '6px 4px', fontSize: 12 };

const hayValor = (v) => v != null && v !== '';
const pesoCria = (c) => (hayValor(c.peso_nacer_cria) ? `${c.peso_nacer_cria} kg` : null);

const CriasCard = ({ crias, config, tipografia }) => {
  const colorPrimary = config.color_primary || '#1a5f2a';
  // En un celular de 375px la card deja ~293px y la tabla de escritorio pedía
  // 393: el peso y el pelo quedaban detrás del scroll. Ahí va más apretada —
  // letra de 12, 4px de aire por lado en cada celda, chip más chico, el peso
  // sin «kg» (la columna ya dice PN)— y el padre parte por palabra sin piso de
  // ancho.
  const screens = Grid.useBreakpoint();
  const movil = !screens.sm;
  if (!crias?.length) return null;

  const titulo = (texto) => (
    <span style={{ color: colorPrimary, fontFamily: tipografia.title, fontSize: 14 }}>{texto}</span>
  );

  if (crias.length === 1) {
    const cria = crias[0];
    const fila = (label, value) =>
      hayValor(value) ? (
        <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #f5f5f5' }}>
          <Text type="secondary" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</Text>
          <Text strong style={{ fontSize: 13 }}>{value}</Text>
        </div>
      ) : null;
    return (
      <Card
        style={{ marginBottom: 16 }}
        styles={{ body: { padding: '20px 24px' } }}
        title={titulo('Cría al Pie')}
        extra={<BadgeSexoCria sexo={cria.sexo_cria} />}
      >
        {fila('RP', cria.rp_cria)}
        {fila('Nacimiento', cria.nacimiento_cria)}
        {fila('Padre', cria.padre_cria)}
        {fila('Peso al nacer', pesoCria(cria))}
        {fila('Pelo', cria.pelo_cria_label)}
        {fila('Registro', cria.reg_cria)}
      </Card>
    );
  }

  const columnas = [
    { key: 'rp', title: 'RP', valor: (c) => c.rp_cria },
    { key: 'sexo', title: 'Sexo', valor: (c) => c.sexo_cria, render: (c) => <BadgeSexoCria sexo={c.sexo_cria} corto movil={movil} /> },
    { key: 'nac', title: 'Nac.', valor: (c) => c.nacimiento_cria },
    { key: 'padre', title: 'Padre', valor: (c) => c.padre_cria, parte: true },
    { key: 'peso', title: 'PN', valor: movil ? (c) => (hayValor(c.peso_nacer_cria) ? String(c.peso_nacer_cria) : null) : pesoCria },
    { key: 'pelo', title: 'Pelo', valor: (c) => c.pelo_cria_label?.[0] },
    { key: 'reg', title: 'Registro', valor: (c) => c.reg_cria },
  ]
    .filter(col => crias.some(c => hayValor(col.valor(c))))
    .map(col => ({
      key: col.key,
      title: col.title,
      // Todo en un renglón menos el padre: es la única columna larga, y si
      // ella no cede, cede la tabla entera con un scroll.
      onHeaderCell: () => ({ style: { whiteSpace: 'nowrap', ...(movil && CELDA_MOVIL) } }),
      onCell: () => ({
        style: {
          ...(col.parte ? (movil ? {} : { minWidth: 110 }) : { whiteSpace: 'nowrap' }),
          ...(movil && CELDA_MOVIL),
        },
      }),
      render: (_, c) => (col.render ? col.render(c) : (col.valor(c) ?? '—')),
    }));

  return (
    <Card
      style={{ marginBottom: 16 }}
      styles={{ body: { padding: movil ? '8px 8px' : '12px 16px' } }}
      title={titulo('Crías al Pie')}
      extra={<Text type="secondary" style={{ fontSize: 13 }}>{crias.length} terneros</Text>}
    >
      {/* `x: true` + layout automático: el navegador parte el padre para que
          entre y SOLO si ni así alcanza aparece el scroll. Con `max-content`
          las columnas no se parten nunca y el scroll salía siempre. En el
          celular va SIN scroll: el modo scroll de antd agrega una fila de
          medición que estira las columnas (la del pelo, de una letra, medía
          48px) y la tabla quedaba 2px más ancha que la card. */}
      <Table
        size="small"
        pagination={false}
        scroll={movil ? undefined : { x: true }}
        tableLayout="auto"
        rowKey={(c) => c.id_cria}
        columns={columnas}
        dataSource={crias}
      />
    </Card>
  );
};

// ═══════════════════════════════════════════════════════════════════
// Servicio — solo para hembras con servicio (tipo 2 y 5)
// ═══════════════════════════════════════════════════════════════════
const ServicioCard = ({ animal, config, tipografia }) => {
  const colorPrimary = config.color_primary || '#1a5f2a';
  const s = animal.servicio;

  if (!s || (!s.tipo && !s.toro && !s.fecha_servicio && !s.estado)) return null;

  const fila = (label, value) =>
    value != null && value !== '' ? (
      <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #f5f5f5' }}>
        <Text type="secondary" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</Text>
        <Text strong style={{ fontSize: 13 }}>{value}</Text>
      </div>
    ) : null;

  return (
    <Card
      style={{ marginBottom: 16 }}
      styles={{ body: { padding: '20px 24px' } }}
      title={<span style={{ color: colorPrimary, fontFamily: tipografia.title, fontSize: 14 }}>Servicio</span>}
    >
      {fila('Tipo', s.tipo)}
      {fila('Toro', s.toro)}
      {fila('Fecha de servicio', s.fecha_servicio)}
      {s.fecha_parto && fila('Parto esperado', fmtFecha(s.fecha_parto))}
      {/* `servicio.estado` es el estado del servicio (PREÑADA / CON SERVICIO),
          no un registro de cria: el catalogo lo titula ESTADO y la tabla de
          lote de esta misma pagina tambien. El registro de la cria es otro
          campo (Cria.reg_cria) y vive en CriasCard. */}
      {fila('Estado', s.estado)}
    </Card>
  );
};


// ═══════════════════════════════════════════════════════════════════
// Árbol genealógico — pedigree horizontal, solo ramas con datos
// Animal (izq) | divider | Padres | Abuelos | Bisabuelos
// Línea paterna arriba · Línea materna abajo
// ═══════════════════════════════════════════════════════════════════
// Se EXPORTA para que la ficha del catalogo de genetica lo use tal cual. No se
// copia: el arbol tiene decisiones finas —los huecos que no se colapsan para
// que el padre caiga entre sus dos abuelos, el modal de pantalla completa en
// mobile— y dos copias divergen en la primera correccion que se le haga a una.
export const ArbolGenealogico = ({ genealogia, config, tipografia }) => {
  const colorPrimary = config.color_primary || '#1a5f2a';
  const g = genealogia;
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md; // <768px (md breakpoint de antd)
  const [modalVisible, setModalVisible] = useState(false);

  // Los huecos NO se colapsan: cada generación conserva sus posiciones aunque
  // falte un nombre. Si se filtran, un abuelo ausente corre a los de abajo y el
  // padre deja de caer entre sus dos progenitores — el mismo defecto que se
  // corrigió en el PDF de carátulas. `null` se dibuja como espacio vacío.
  const generaciones = [
    [
      g.padres?.padre  && { label: 'Padre',  value: g.padres.padre,  linea: 'P' },
      g.padres?.madre  && { label: 'Madre',  value: g.padres.madre,  linea: 'M' },
    ],
    [
      g.abuelos?.paterno?.abuelo && { label: 'Abuelo P.',  value: g.abuelos.paterno.abuelo,  linea: 'P' },
      g.abuelos?.paterno?.abuela && { label: 'Abuela P.',  value: g.abuelos.paterno.abuela,  linea: 'P' },
      g.abuelos?.materno?.abuelo && { label: 'Abuelo M.',  value: g.abuelos.materno.abuelo,  linea: 'M' },
      g.abuelos?.materno?.abuela && { label: 'Abuela M.',  value: g.abuelos.materno.abuela,  linea: 'M' },
    ],
    [
      g.bisabuelos?.paterno?.bisabuelo && { label: 'Bisabuelo P.',  value: g.bisabuelos.paterno.bisabuelo,  linea: 'P' },
      g.bisabuelos?.paterno?.bisabuela && { label: 'Bisabuela P.',  value: g.bisabuelos.paterno.bisabuela,  linea: 'P' },
      g.bisabuelos?.materno?.bisabuelo && { label: 'Bisabuelo M.',  value: g.bisabuelos.materno.bisabuelo,  linea: 'M' },
      g.bisabuelos?.materno?.bisabuela && { label: 'Bisabuela M.',  value: g.bisabuelos.materno.bisabuela,  linea: 'M' },
    ],
  ].map(gen => gen.map(n => n || null))
   .filter(gen => gen.some(Boolean));

  if (generaciones.length === 0) return null;

  // Funciones que se INVOCAN, no componentes que se montan: definir un
  // componente dentro del render de otro lo hace remontar en cada render del
  // padre (aca, en cada toggle del modal). Ver la misma nota en SiteHeader.
  const nodo = ({ label, value, linea }) => (
    <div style={{
      // flex:1 reparte la altura de la columna en partes iguales y el nodo se
      // centra dentro de la suya. Así el padre (1 de 2 → mitad superior) cae
      // exactamente en el medio de sus dos abuelos (1 de 4 cada uno), que es la
      // alineación que faltaba.
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      minHeight: 44,
      padding: '2px 0',
    }}>
      <div style={{
        width: '100%',
        border: `1px solid ${linea === 'P' ? colorPrimary + '55' : '#d9d9d9'}`,
        borderLeft: `3px solid ${linea === 'P' ? colorPrimary : '#aaa'}`,
        borderRadius: '0 6px 6px 0',
        padding: '5px 4px 5px 6px', // L/R: 6/4 (antes 10/10) para que nombres largos quepan
        background: '#fafafa',
        overflow: 'hidden',
        fontFamily: tipografia.body,
      }}>
        <div style={{ fontSize: 10, color: linea === 'P' ? colorPrimary : '#888', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.3px', lineHeight: 1.2 }}>
          {label}
        </div>
        <div
          title={value}
          style={{ fontSize: 12, fontWeight: 600, color: '#1a1a1a', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 2 }}
        >
          {value}
        </div>
      </div>
    </div>
  );

  const leyenda = () => (
    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 12, fontSize: 11, color: '#888', fontFamily: tipografia.body }}>
      <span style={{ color: colorPrimary, fontWeight: 600 }}>↑ Línea paterna</span>
      <span>↓ Línea materna</span>
    </div>
  );

  // Render del árbol horizontal con N generaciones (reusado en Card y Modal)
  const renderArbol = (gens) => (
    // alignItems stretch (no 'center'): las columnas tienen que medir todas lo
    // mismo para que las fracciones de cada generación coincidan entre sí.
    <div style={{ display: 'flex', alignItems: 'stretch', gap: 0, minWidth: 'max-content' }}>
      {/* El bloque del propio animal se omite: se sobreentiende que es su árbol
          y así ganamos espacio. La línea paterna va arriba y la materna abajo. */}

      {/* Generaciones — solo las que tienen datos */}
      {gens.map((gen, gi) => (
        <div key={gi} style={{ display: 'flex', flexDirection: 'column', alignSelf: 'stretch', minWidth: 145, flexShrink: 0, marginRight: gi < gens.length - 1 ? 16 : 0 }}>
          {/* `item` y no `nodo`: `nodo` es la funcion que arma el bloque.
              Un slot vacío ocupa su lugar igual (flex:1) para no correr los de
              abajo cuando a la rama le falta un nombre. */}
          {gen.map((item, ni) => (
            <React.Fragment key={ni}>
              {item ? nodo(item) : <div style={{ flex: 1, minHeight: 44 }} />}
            </React.Fragment>
          ))}
        </div>
      ))}
    </div>
  );

  // En mobile: padres + abuelos inline (2 generaciones) + boton "Ver completo"
  // si hay mas. Antes era 1 sola gen porque el bloque del animal ocupaba lugar;
  // al quitarlo, extendemos una generacion mas para que no quede tan vacio.
  const gensInline = isMobile ? generaciones.slice(0, 2) : generaciones;
  const mostrarBotonVerCompleto = isMobile && generaciones.length > 2;

  return (
    <>
      <Card
        style={{ marginBottom: 24 }}
        styles={{ body: { padding: '16px 16px 12px', overflowX: 'auto' } }}
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
            <span style={{ color: colorPrimary, fontFamily: tipografia.title }}>Árbol Genealógico</span>
            {mostrarBotonVerCompleto && (
              <Button
                type="link"
                size="small"
                icon={<FullscreenOutlined />}
                onClick={() => setModalVisible(true)}
                style={{ color: colorPrimary, padding: 0, fontWeight: 600 }}
              >
                Ver completo
              </Button>
            )}
          </div>
        }
      >
        {leyenda()}
        {renderArbol(gensInline)}
      </Card>

      <Modal
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        title={<span style={{ color: colorPrimary, fontFamily: tipografia.title }}>Árbol Genealógico</span>}
        width="100vw"
        style={{ top: 0, maxWidth: '100vw', margin: 0, padding: 0 }}
        styles={{ body: { padding: 16, height: 'calc(100vh - 110px)', overflow: 'auto' } }}
      >
        {leyenda()}
        {renderArbol(generaciones)}
      </Modal>
    </>
  );
};

// ═══════════════════════════════════════════════════════════════════
// Genealogía en tabla — alternativa a ArbolGenealogico, misma INFO en dos
// columnas (línea paterna | línea materna) en vez de ramas horizontales.
// La elige el productor con config.genealogia_vista (ver EstructuraTab.jsx
// del panel). A diferencia del árbol, una tabla NO necesita conservar los
// huecos entre generaciones para alinear columnas: cada fila sin dato
// simplemente no se dibuja, igual que ya hace TablaGenetica con sus DEP's.
// ═══════════════════════════════════════════════════════════════════
const TablaGenealogia = ({ genealogia, config, tipografia }) => {
  const colorPrimary = config.color_primary || '#1a5f2a';
  const g = genealogia;

  // EL ORDEN ES EL DE UNA ESCUADRA DE PEDIGREE LEÍDA DE ARRIBA ABAJO, no el de
  // "más cercano primero": el padre, resaltado, al medio; hacia arriba la
  // rama de machos (abuelo y, sobre él, bisabuelo) y hacia abajo la de
  // hembras (abuela y, bajo ella, bisabuela). Es como lee la genealogía el
  // sector (feedback de Facundo, 2026-10-15) sin armar la escuadra con
  // celdas: la lista compacta sigue siendo lo que entra en un celular y en el
  // catálogo PDF, y el árbol con ramas ya existe como otra vista. El modelo
  // NO dice de quién es hijo cada bisabuelo; este orden copia la alineación
  // del árbol (`ArbolGenealogico`: bisabuelo a la altura del abuelo, bisabuela
  // a la de la abuela), que es la que el comprador ya vio.
  const paterna = [
    { label: 'Bisabuelo P.', value: g.bisabuelos?.paterno?.bisabuelo },
    { label: 'Abuelo P.',    value: g.abuelos?.paterno?.abuelo },
    { label: 'Padre',        value: g.padres?.padre, principal: true },
    { label: 'Abuela P.',    value: g.abuelos?.paterno?.abuela },
    { label: 'Bisabuela P.', value: g.bisabuelos?.paterno?.bisabuela },
  ].filter(f => f.value);

  const materna = [
    { label: 'Bisabuelo M.', value: g.bisabuelos?.materno?.bisabuelo },
    { label: 'Abuelo M.',    value: g.abuelos?.materno?.abuelo },
    { label: 'Madre',        value: g.padres?.madre, principal: true },
    { label: 'Abuela M.',    value: g.abuelos?.materno?.abuela },
    { label: 'Bisabuela M.', value: g.bisabuelos?.materno?.bisabuela },
  ].filter(f => f.value);

  // Sin ningún dato de ningún lado, no hay nada que mostrar — igual criterio
  // que el árbol (`generaciones.length === 0 → null`).
  if (paterna.length === 0 && materna.length === 0) return null;

  // La etiqueta NO se achica ni se parte: con un nombre largo a la derecha,
  // el flex la aplastaba y "PADRE" salía como "PAD / RE" (captura del usuario,
  // 2026-10-15). Ancho fijo para la etiqueta y el nombre se lleva el resto.
  // La fila de Padre/Madre va resaltada con fondo gris y el nombre un punto
  // más grande: es la generación que se mira primero (idea de Facundo,
  // aplicada acá y no en una escuadra, que duplicaría al árbol).
  const fila = ({ label, value, principal }) => (
    <div
      key={label}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: principal ? '8px 8px' : '6px 0',
        borderBottom: '1px solid #f5f5f5',
        ...(principal ? { background: '#f5f5f5', borderRadius: 6, margin: '0 -8px' } : {}),
      }}
    >
      <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, fontFamily: tipografia.body, flexShrink: 0, minWidth: 84, whiteSpace: 'nowrap' }}>
        {label}
      </Text>
      {/* A la izquierda, no a la derecha: con nombres de dos renglones, el
          alineado a la derecha dejaba el texto "colgando" lejos de su etiqueta
          (pedido del usuario, 2026-10-15). */}
      <Text strong style={{ fontSize: principal ? 14 : 13, textAlign: 'left', flex: 1, fontFamily: tipografia.body }}>
        {value}
      </Text>
    </div>
  );

  // `Col xs={24} md={12}` apila en mobile con el MISMO corte (768px) que usa
  // `Grid.useBreakpoint().md` en ArbolGenealogico — no hace falta duplicar el
  // hook, el grid de antd ya corta ahí.
  // Los títulos de línea pesan lo mismo los dos (antes la materna iba en gris
  // claro y se leía como secundaria): el color distingue, el peso no.
  const columna = (titulo, filas, linea) => filas.length > 0 && (
    <Col xs={24} md={12} key={titulo}>
      <div style={{
        fontSize: 13, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.6,
        marginBottom: 8, paddingBottom: 6, borderBottom: `2px solid ${linea === 'P' ? colorPrimary : '#555'}`,
        color: linea === 'P' ? colorPrimary : '#555', fontFamily: tipografia.body,
      }}>
        {titulo}
      </div>
      {filas.map(fila)}
    </Col>
  );

  return (
    <Card
      style={{ marginBottom: 24 }}
      // "Genealogía" y no "Árbol Genealógico": una tabla titulada árbol
      // confunde, y el productor eligió justamente NO ver el árbol.
      title={<span style={{ color: colorPrimary, fontFamily: tipografia.title }}>Genealogía</span>}
    >
      <Row gutter={[24, 16]}>
        {columna('Línea paterna', paterna, 'P')}
        {columna('Línea materna', materna, 'M')}
      </Row>
    </Card>
  );
};

// ═══════════════════════════════════════════════════════════════════
// Tabla DEPs / Precisión / Ranking — 3 secciones independientes
// Rediseñado 2026-06-24 para sumar 4 indicadores (AOB, GD, GC, %GI)
// sin romper en mobile. La columna de etiqueta de fila ahora es la
// CABECERA PRINCIPAL de cada sección. Eso libera 1 columna y deja
// 10 indicadores con el mismo ancho.
// ═══════════════════════════════════════════════════════════════════
const COLS_GENETICA = [
  { key: 'nacer',      label: 'PN',   title: 'Peso Nacer' },
  { key: 'destete',    label: 'PD',   title: 'Peso Destete' },
  { key: 'ap_materna', label: 'AM',   title: 'Aptitud Materna' },
  { key: 'lyc',        label: 'L&C',  title: 'Leche y Conformación' },
  { key: 'final',      label: 'PF',   title: 'Peso Final' },
  { key: 'ce',         label: 'CE',   title: 'Circunferencia Escrotal' },
  { key: 'aob',        label: 'AOB',  title: 'Área de Ojo de Bife' },
  { key: 'gd',         label: 'GD',   title: 'Ganancia Diaria' },
  { key: 'gc',         label: 'GC',   title: 'Ganancia de Carcasa' },
  { key: 'porcgi',     label: '%GI',  title: '% Grasa Intramuscular' },
];

// UN solo título para las dos vistas de genética (tabla y gráfico): es la
// misma decisión del productor en dos formatos y tiene que leerse igual.
// "Información Genética" no decía qué había adentro (feedback de Facundo,
// 2026-10-15).
const TITULO_GENETICA = "DEP's · Precisión · Ranking";

const TablaGenetica = ({ animal, config, tipografia }) => {
  const colorPrimary = config.color_primary || '#1a5f2a';
  const { deps, precision, ranking } = animal;

  const tieneAlgo = (obj) => obj && Object.values(obj).some(v => v != null);
  if (!tieneAlgo(deps) && !tieneAlgo(precision) && !tieneAlgo(ranking)) return null;

  // Un DEP que la cabaña no evalúa no es un dato faltante: es una columna que
  // no existe para ESTE animal. Se oculta la columna entera (deps, precisión
  // Y ranking los tres en null) en vez de dibujarla llena de guiones — una
  // columna de — de punta a punta no informa, ocupa un lugar que podría tener
  // otro DEP al lado.
  const cols = COLS_GENETICA.filter(col =>
    deps?.[col.key] != null || precision?.[col.key] != null || ranking?.[col.key] != null
  );
  if (cols.length === 0) return null;

  // Las 3 secciones se renderizan en orden fijo: fila 1 = DEPs, fila 2 =
  // Precision, fila 3 = Ranking. Si una serie no tiene datos se renderiza
  // con todos los valores en — (no se omite la fila para no romper la
  // correspondencia visual con el banner de arriba).
  const filasOrden = [
    { titulo: "DEP'S",     datos: deps },
    { titulo: 'PRECISIÓN', datos: precision },
    { titulo: 'RANKING',   datos: ranking },
  ];

  const cellBase = {
    padding: '10px 8px',
    textAlign: 'center',
    fontSize: 13,
    minWidth: 60,
  };

  return (
    <Card
      style={{ marginBottom: 24 }}
      title={<span style={{ color: colorPrimary, fontFamily: tipografia.title }}>{TITULO_GENETICA}</span>}
      styles={{ body: { padding: '0 0 1px' } }}
    >
      {/* Cabecera: un solo título (no columnas). Antes eran 3 divs justificados
          con barras verticales y se percibían como columnas en vez de título. */}
      <div
        style={{
          background: colorPrimary,
          color: '#fff',
          fontFamily: tipografia.title,
          fontWeight: 700,
          fontSize: 13,
          letterSpacing: '1px',
          padding: '10px 14px',
          textAlign: 'center',
        }}
      >
        DEP'S / Precisión / Ranking
      </div>

      {/* Estilos responsive: en mobile bajamos padding L/R de 8 a 2px en
          thead/tbody y reducimos minWidth de 60 a 30 para que las 10
          columnas entren mejor sin scroll (o con scroll mucho menos
          pronunciado). El font-size del thead tambien baja un punto. */}
      <style>{`
        @media (max-width: 991px) {
          .puja-tabla-genetica thead th,
          .puja-tabla-genetica tbody td {
            padding-left: 2px !important;
            padding-right: 2px !important;
            min-width: 30px !important;
            font-size: 12px !important;
          }
          .puja-tabla-genetica thead th {
            font-size: 10px !important;
            letter-spacing: 0.2px !important;
          }
        }
      `}</style>

      {/* Tabla: cabecera secundaria con los 10 indicadores + 3 filas
          (DEPs, Precisión, Ranking) en ese orden fijo. Sin columna de
          etiqueta de fila — el usuario lo lee por el orden + el banner. */}
      <div style={{ overflowX: 'auto' }}>
        <table
          className="puja-tabla-genetica"
          style={{ width: '100%', borderCollapse: 'collapse', fontFamily: tipografia.body }}
        >
          <thead>
            <tr style={{ background: '#fafafa' }}>
              {cols.map(col => (
                <th
                  key={col.key}
                  title={col.title}
                  style={{
                    ...cellBase,
                    color: '#666',
                    fontWeight: 600,
                    borderBottom: '1px solid #f0f0f0',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontSize: 11,
                  }}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filasOrden.map(({ titulo, datos }, i) => (
              <tr key={titulo} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                {cols.map(col => (
                  <td key={col.key} style={{ ...cellBase, color: '#1a1a1a', borderBottom: '1px solid #f5f5f5' }}>
                    {datos?.[col.key] != null
                      ? <span style={{ fontWeight: 500 }}>{datos[col.key]}</span>
                      : <span style={{ color: '#ccc' }}>—</span>
                    }
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

// ═══════════════════════════════════════════════════════════════════
// DEP's como gráfico de barras por percentil — alternativa a TablaGenetica,
// misma elección del productor que `genealogia_vista` (config.deps_vista).
//
// GEMELO de `BarraDep` (BloqueToro.jsx, portada del catálogo de genética) y
// `FilaDep` (AnimalCatalogoPage.jsx, ficha del catálogo): misma lectura
// visual —barra llena a `100 - percentil`%, color por escalón vía
// `escalonDe` (fuente ÚNICA de la escala, utils/depsEscalones.js)— pero
// armada sobre OTRA forma de dato. El catálogo de genética ya manda una
// LISTA `[{clave, etiqueta, valor, percentil}]` armada en el backend
// (`_deps_publicos`, micrositio_routes.py); la ficha del remate trae
// `deps`/`precision`/`ranking` como TRES OBJETOS planos, uno por clave de
// COLS_GENETICA. Unificar los dos hubiera significado tocar el endpoint del
// catálogo (fuera del alcance de esta tarea) solo para compartir un
// componente de 40 líneas — se prefirió el gemelo chico, con `escalonDe`
// como el único punto que SÍ comparten.
// ═══════════════════════════════════════════════════════════════════
const FilaDepFicha = ({ dep, color }) => {
  const conRank = dep.percentil != null;
  const lleno = conRank ? Math.max(0, Math.min(100, 100 - dep.percentil)) : 0;
  const escalon = conRank ? escalonDe(dep.percentil) : null;

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
        <Text style={{ fontSize: 13 }} title={dep.titulo}>{dep.etiqueta}</Text>
        {/* "1.7  –  Prec. 0.73  –  Rank 97%": precisión y rank a 13 y en
            color de texto, no a 11 y en gris, que se perdían (feedback de
            Facundo, 2026-10-15). El DEP sigue a 14 negrita y el rank en su
            pastilla de escalón: son el dato y la señal; prec. y rank lo
            califican, y una fila con los tres al mismo peso es una pared de
            números. Los guiones con aire a los lados reemplazan al punto medio. */}
        <Text style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
          {/* `!= null` para que un DEP de 0 —la línea base de la raza— se
              muestre en vez de caerse. */}
          <Text strong style={{ fontSize: 14 }}>
            {dep.valor != null ? dep.valor : '—'}
          </Text>
          {dep.precision != null && (
            <>
              <Text type="secondary" style={{ margin: '0 10px' }}>–</Text>
              <Text style={{ fontSize: 13 }}>Prec. {dep.precision}</Text>
            </>
          )}
          {/* SIN RANKING NO HAY BARRA (no hay percentil que dibujar) pero el
              valor/precisión de arriba se muestran igual. */}
          {conRank && (
            <>
              <Text type="secondary" style={{ margin: '0 10px' }}>–</Text>
              {escalon ? (
                <Text
                  strong
                  style={{
                    fontSize: 13,
                    color: escalon.color,
                    ...(escalon.fondo
                      ? { background: escalon.fondo, padding: '2px 7px', borderRadius: 4 }
                      : {}),
                  }}
                  title={`${escalon.etiqueta} — rank ${dep.percentil}% de la raza`}
                >
                  Rank {dep.percentil}%
                </Text>
              ) : (
                <Text style={{ fontSize: 13 }}>Rank {dep.percentil}%</Text>
              )}
            </>
          )}
        </Text>
      </div>
      {conRank && (
        <div style={{ height: 6, background: '#f0f0f0', borderRadius: 3, overflow: 'hidden' }}>
          <div
            style={{
              width: `${lleno}%`, height: '100%', borderRadius: 3,
              // Fuera de escalón se queda con el acento del productor, no con
              // un gris: realzar necesita una línea de base de la que sobresalir.
              background: escalon ? escalon.color : color,
            }}
          />
        </div>
      )}
    </div>
  );
};

const GraficoDeps = ({ animal, config, tipografia }) => {
  const colorPrimary = config.color_primary || '#1a5f2a';
  const { deps, precision, ranking } = animal;

  // COLS_GENETICA → {clave, etiqueta, valor, percentil, precision}, la forma
  // que espera FilaDepFicha. Se descarta la fila entera si no hay NADA (ni
  // valor, ni precisión, ni rank): un DEP que la cabaña no evalúa no es un
  // dato faltante, es una fila que no existe para este animal.
  const filas = COLS_GENETICA
    .map(col => ({
      clave: col.key,
      etiqueta: col.label,
      titulo: col.title,
      valor: deps?.[col.key] ?? null,
      percentil: ranking?.[col.key] ?? null,
      precision: precision?.[col.key] ?? null,
    }))
    .filter(d => d.valor != null || d.percentil != null || d.precision != null);

  if (filas.length === 0) return null;

  return (
    <Card
      style={{ marginBottom: 24 }}
      title={<span style={{ color: colorPrimary, fontFamily: tipografia.title }}>{TITULO_GENETICA}</span>}
    >
      {filas.map(dep => (
        <FilaDepFicha key={dep.clave} dep={dep} color={colorPrimary} />
      ))}
      {/* Misma nota al pie que el catálogo de genética: el instrumento es el
          mismo y se lee igual en los dos lugares. */}
      <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
        El percentil es el lugar del animal dentro de su raza: 1 es el mejor.
      </Text>
    </Card>
  );
};

// ═══════════════════════════════════════════════════════════════════
// Compañeros del corral
// ═══════════════════════════════════════════════════════════════════
const CompanerosCorral = ({ companeros, slug, config, tipografia }) => {
  const colorPrimary = config.color_primary || '#1a5f2a';

  return (
    <Card
      style={{ marginBottom: 24 }}
      title={<span style={{ color: colorPrimary, fontFamily: tipografia.title }}>Otros animales del {terminoLote(config).minus}</span>}
    >
      <Row gutter={[12, 12]}>
        {companeros.map(c => {
          const imgUrl = imagenAnimalUrl(c, config);
          return (
          <Col key={c.id_animal} xs={12} sm={8}>
            <Link to={`/remate/${slug}/animal/${buildAnimalSlug(c)}`} style={{ display: 'block' }}>
              <Card
                hoverable
                styles={{ body: { padding: 10 } }}
                cover={
                  imgUrl ? (
                    <img src={imgUrl} alt={c.nom_animal}
                      style={{ height: 100, objectFit: 'cover' }} />
                  ) : (
                    <div style={{ height: 100, background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bbb', fontSize: 11 }}>
                      Sin imagen
                    </div>
                  )
                }
              >
                <Text strong style={{ fontSize: 12, display: 'block', color: colorPrimary }}>{c.nom_animal}</Text>
                <Text type="secondary" style={{ fontSize: 11 }}>RP: {c.rp_animal}</Text>
              </Card>
            </Link>
          </Col>
          );
        })}
      </Row>
    </Card>
  );
};


// ═══════════════════════════════════════════════════════════════════
// Pre-oferta
// ═══════════════════════════════════════════════════════════════════
// Reemplaza los tags del template con datos del animal y el evento
const interpolarMensaje = (texto, animal, evento) => {
  if (!texto) return '';
  return texto
    .replace(/\[nom_animal\]/g, animal.nom_animal   || '')
    .replace(/\[corral\]/g,     animal.corral       || '')
    .replace(/\[raza\]/g,       animal.nom_raza     || '')
    .replace(/\[categoria\]/g,  animal.nombre_categoria || animal.categoria?.nom_categoria || '')
    .replace(/\[nom_evento\]/g, evento.nombre_evento || '');
};

// Formatea fecha: 'dd/mm/aaaa' en desktop, 'dd/mm' en mobile.
// El CSS-only no puede acortar el contenido textual, asi que renderizamos
// dos spans (full vs short) y mostramos/ocultamos por media query.
const FechaOferta = ({ v }) => {
  if (!v) return <span>—</span>;
  const d = new Date(v + 'T00:00:00');
  const full = d.toLocaleDateString('es-AR');                                      // 23/06/2026
  const short = d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' }); // 23/06
  return (
    <>
      <span className="ofertas-fecha-full">{full}</span>
      <span className="ofertas-fecha-short">{short}</span>
    </>
  );
};

const OFERTAS_COLS = [
  {
    title: '#',
    key: 'pos',
    width: 48,
    render: (_, __, idx) => idx + 1,
  },
  {
    // Reserva de identidad: en el catalogo publico mostramos el ID en
    // lugar del nombre del ofertante. El usuario propietario de la
    // oferta puede verificar su propio ID en su perfil/historial.
    title: 'Usuario',
    dataIndex: 'id_usuario',
    key: 'id_usuario',
    width: 80,
    render: v => v ? `#${v}` : '—',
  },
  {
    title: 'Fecha',
    dataIndex: 'fecha_oferta',
    key: 'fecha_oferta',
    width: 110,
    render: v => <FechaOferta v={v} />,
  },
  {
    title: 'Hora',
    dataIndex: 'hora_oferta',
    key: 'hora_oferta',
    width: 80,
    // En mobile sacamos los segundos: 14:35:22 -> 14:35
    render: v => v ? v.slice(0, 5) : '—',
  },
  {
    title: 'Importe',
    dataIndex: 'monto_oferta',
    key: 'monto_oferta',
    width: 140,
    align: 'right',
    // Number(v) y no v.toLocaleString(): si el monto llega como string (una
    // columna Numeric puede serializarse asi), String.toLocaleString ignora los
    // argumentos y devuelve la cadena cruda — "$3000000.00" en la columna de
    // plata. El resto del archivo ya castea igual antes de operar con montos.
    render: v => v != null
      ? `$${Number(v).toLocaleString('es-AR', { minimumFractionDigits: 0 })}`
      : '—',
  },
];

const TablaOfertas = ({ ofertas, config, tipografia }) => {
  if (!ofertas?.length) return null;
  const colorPrimary = config.color_primary || '#1a5f2a';
  return (
    <Card
      size="small"
      title={
        <span style={{ fontFamily: tipografia.title, color: colorPrimary, fontWeight: 700 }}>
          Historial de ofertas
        </span>
      }
      style={{ borderRadius: 10 }}
      className="puja-tabla-ofertas"
    >
      {/* Estilos responsive: en mobile reducimos padding L/R, font, y
          cambiamos la fecha por su version corta. Mismo patron que la
          TablaGenetica. Los selectores son los th/td de antd Table size=small.

          Importante: las columnas Fecha/Hora/Importe tienen `width` en
          OFERTAS_COLS que antd traduce a `min-width` en el <colgroup><col>.
          En mobile sobreescribimos min-width:0 para que las celdas se
          contraigan al ancho del contenido (que ya es chico: 23/06, 14:35,
          $1.000.000). Sin esto, el ancho fijo dejaba espacio inutil
          incluso con padding 4px. */}
      <style>{`
        .puja-tabla-ofertas .ofertas-fecha-short { display: none; }
        @media (max-width: 991px) {
          .puja-tabla-ofertas .ant-table-thead > tr > th,
          .puja-tabla-ofertas .ant-table-tbody > tr > td {
            padding-left: 4px !important;
            padding-right: 4px !important;
            font-size: 12px !important;
          }
          .puja-tabla-ofertas .ant-table-thead > tr > th {
            font-size: 11px !important;
            letter-spacing: 0.2px !important;
          }
          .puja-tabla-ofertas .ofertas-fecha-full  { display: none; }
          .puja-tabla-ofertas .ofertas-fecha-short { display: inline; }
          /* Libera los width fijos: las columnas se ajustan al contenido. */
          .puja-tabla-ofertas .ant-table colgroup col {
            min-width: 0 !important;
            width: auto !important;
          }
        }
      `}</style>
      <Table
        dataSource={ofertas}
        columns={OFERTAS_COLS}
        rowKey="id_oferta"
        size="small"
        pagination={false}
        scroll={{ x: 'max-content' }}
        rowClassName={(_, idx) => idx === 0 ? 'oferta-top-row' : ''}
        style={{ fontFamily: tipografia.body }}
      />
    </Card>
  );
};

const PreOfertaCard = ({ animal, evento, config, tipografia, isAuthenticated,
                        usuarioId = null,
                        mejorOferta: mejorOfertaProp = null, onOfertaCreada,
                        onOfertandoChange }) => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [ofertando, setOfertando] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [montoConfirmar, setMontoConfirmar] = useState(null);
  const [mejorOferta, setMejorOferta] = useState(mejorOfertaProp); // { monto, id_usuario } | null
  // Condiciones del remate: se piden una única vez por evento, recién cuando la
  // persona intenta ofertar. `montoPendiente` guarda la oferta que quedó trunca
  // para reintentarla sola después de aceptar.
  const [condicionesVisible, setCondicionesVisible] = useState(false);
  const [condiciones, setCondiciones] = useState(null);
  const [aceptando, setAceptando] = useState(false);
  const [montoPendiente, setMontoPendiente] = useState(null);
  // Alguien ocupó el peldaño mientras esta oferta viajaba. Guarda lo que manda
  // el backend (mejor_oferta + sugerido ya calculado) para ofrecer re-ofertar
  // con un sí/no. NO se vuelven a mostrar las condiciones: las acaba de aceptar,
  // y meterle un textazo cuando lo que corre son segundos sería sacarle de las
  // manos justo lo que vino a buscar.
  const [superado, setSuperado] = useState(null);
  // Habilitación de la cabaña + condiciones, en una sola llamada. Se consulta
  // ANTES de que la persona escriba nada: tipear un monto, apretar y recién ahí
  // comerse un 403 es la peor forma de enterarse de que no te habilitaron.
  const [acceso, setAcceso] = useState(null);
  const colorPrimary = config.color_primary || '#1a5f2a';
  const colorBtn     = config.color_btn_primary || colorPrimary;

  const precioBase = Number(animal.precio_base || 0);
  const salto      = Number(animal.salto_oferta || 0);

  // El salto es el PELDAÑO de la escalera, no un piso: con base 1.000.000 y
  // salto 500.000 las ofertas validas son 1.000.000, 1.500.000, 2.000.000, y
  // 1.234.567 no es una oferta valida en un remate aunque supere el minimo. La
  // escalera se ancla al PRECIO BASE, no al cero.
  //
  // Esta cuenta espeja `minimo_valido()` / `es_escalon_valido()` del backend
  // (oferta_routes.py) a proposito: el backend es el que decide, pero si el
  // formulario deja tipear un monto que el backend va a rechazar, la persona se
  // entera despues de apretar Ofertar. La tolerancia cubre el error de float:
  // los montos vienen del JSON como Number y un modulo de 3000000.00 puede dar
  // 0.0000001 en vez de 0.
  // Las cuentas viven en utils/escalonOferta.js, compartidas con el panel del
  // comprador: tenerlas copiadas ya salió caro una vez con el formatter del
  // monto, que se arregló acá y quedó vivo allá.
  const enEscalon        = (valor) => enEscalonBase(valor, precioBase, salto);
  const escalonSiguiente = (valor) => escalonSiguienteBase(valor, precioBase, salto);
  // Primera oferta: minimo = precio base (se puede igualar). Siguientes: el
  // primer peldaño por encima de la mejor oferta.
  // Sin salto configurado no hay escalera, pero el monto igual tiene que
  // SUPERAR al anterior: desde que existe UNIQUE (id_animal, monto_oferta)
  // repetir el monto rebota en la base. Mismo 0.01 que usa minimo_valido().
  //
  // Una "mejor oferta" por DEBAJO del precio base no existe en el dominio: la
  // primera oferta ya tiene que igualar la base. Si llega igual —pasaba cuando
  // el backend mandaba `mejor_oferta: null` por RLS y Number(null) daba 0— se
  // ignora y se trata como si no hubiera ofertas. Sin este guard la card
  // mostraba "$0 · Última oferta" y el input quedaba precargado con el SALTO,
  // porque la escalera anclada a la base no está definida por debajo de ella:
  // escalonSiguiente(0) con base 10.000.000 y salto 250.000 devolvía 250.000.
  const ofertaValida = mejorOferta !== null && Number(mejorOferta.monto) >= precioBase;

  // La mejor oferta ya es de esta persona: si oferta de nuevo se supera a si
  // misma y sube el precio sin que nadie la haya pujado. Se avisa ANTES de que
  // apriete, no despues: enterarse de que tiraste plata contra vos mismo
  // cuando la oferta ya entro no sirve de nada.
  const yaLidero = ofertaValida
    && usuarioId != null
    && String(mejorOferta.id_usuario) === String(usuarioId);

  const precioMin = !ofertaValida
    ? precioBase
    : (salto > 0 ? escalonSiguiente(mejorOferta.monto) : mejorOferta.monto + 0.01);

  // UNA sola fuente de verdad de si el <Form> esta montado, usada por el render
  // y por el efecto de abajo. Estaban separados y ya divergieron una vez: se
  // agrego la rama de "habilitacion pendiente" -que tambien desmonta el Form- y
  // el guard del efecto no se entero. Tocar la instancia sin Form montado tira
  // "Instance created by useForm is not connected to any Form element".
  // El estado de la ventana lo decide el BACKEND (evento.estado_ofertas): si lo
  // recalculara el front con el reloj del visitante, alguien con la compu
  // atrasada veria el formulario abierto cuando el backend va a rechazar.
  const estadoOfertas   = evento.estado_ofertas || {};
  const ofertasPendientes = estadoOfertas.estado === 'programadas';
  const ofertasCerradas   = estadoOfertas.estado === 'cerradas';

  // Funcion INVOCADA, no componente montado: se usa en el display normal y en el
  // cartel de ofertas programadas, y definir un componente dentro del render del
  // padre lo remontaria en cada render (Skills/react-rules.md § Prohibiciones).
  // Con la ventana CERRADA lo que importa es en cuánto quedó la puja, no en
  // cuánto arrancó: mostrar el precio de apertura sobre un remate terminado
  // esconde justo el dato que todos vienen a buscar. Si no hubo ninguna oferta
  // sí se muestra la base, que ahí es la única información que existe.
  const cerroConOferta = ofertasCerradas && ofertaValida;

  const bloquePrecioBase = () => (
    <>
      <div style={{ fontSize: 22, fontWeight: 700, color: cerroConOferta ? '#1677ff' : colorPrimary }}>
        ${(cerroConOferta ? mejorOferta.monto : precioBase).toLocaleString('es-AR')}
      </div>
      <Text type="secondary" style={{ fontSize: 12 }}>
        {cerroConOferta ? 'Oferta ganadora' : 'Precio base'}
      </Text>
      {salto > 0 && (
        <div style={{ marginTop: 4 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Salto de oferta: <strong>${salto.toLocaleString('es-AR')}</strong>
          </Text>
        </div>
      )}
    </>
  );

  const bloqueadoPorAcceso = isAuthenticated && acceso && !acceso.autorizado;
  const mostrarFormulario  = !evento.es_finalizado && !ofertasPendientes
                             && !ofertasCerradas && !bloqueadoPorAcceso;

  // Precarga el input con el mínimo cada vez que cambia (nueva oferta registrada).
  useEffect(() => {
    if (!mostrarFormulario) return;
    form.setFieldsValue({ monto: precioMin });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [precioMin, mostrarFormulario]);

  useEffect(() => {
    if (!isAuthenticated || !evento?.id_evento) { setAcceso(null); return; }
    let vigente = true;
    buyerService.getAccesoRemate(evento.id_evento)
      .then(({ data }) => { if (vigente) setAcceso(data); })
      // Silencioso a propósito: si falla, se cae al comportamiento anterior
      // (dejar ofertar y que el backend responda). Molestar con un error por
      // no poder pintar un cartel sería peor que no pintarlo.
      .catch(() => {});
    return () => { vigente = false; };
  }, [isAuthenticated, evento?.id_evento]);

  const esAgrupado = animal.agrupado === 1;
  // Selecciona el mensaje según el tipo de animal
  const mensajeActivo = esAgrupado ? evento.mensaje_oferta_2 : evento.mensaje_oferta_1;
  const tieneMensajes = !!mensajeActivo;

  // Si el evento tiene mensaje configurado para este tipo, muestra el modal antes de confirmar
  const handleFormSubmit = (values) => {
    if (!isAuthenticated) {
      message.warning('Debés iniciar sesión para ofertar');
      return;
    }
    // Superarse a si mismo SIEMPRE pasa por el modal, tenga o no mensajes el
    // evento: es el unico caso donde la oferta no compite contra nadie y le
    // sube el precio a la persona contra si misma. Un click de mas no puede
    // costarle un salto.
    if (tieneMensajes || yaLidero) {
      setMontoConfirmar(values.monto);
      setModalVisible(true);
    } else {
      confirmarOferta(values.monto);
    }
  };

  /**
   * Guarda la mejor oferta SOLO si es un monto creíble.
   *
   * El backend puede mandar `mejor_oferta: null` —pasaba cuando RLS le tapaba
   * las ofertas ajenas— y `Number(null)` da 0. Ese 0 se guardaba como si fuera
   * la oferta líder y dejaba el formulario en un estado imposible: la card
   * mostrando "$0 · Última oferta" y el input precargado con el salto en vez
   * del precio base. Y no se limpiaba al cerrar el modal, así que el formulario
   * quedaba roto incluso después de apretar "No, dejar así".
   *
   * Ante un valor que no llega al precio base se conserva el estado anterior:
   * un dato viejo pero coherente es mejor que uno nuevo imposible.
   */
  const actualizarMejorOferta = (montoNuevo, idUsuario, fallback = null) => {
    const valor = Number(montoNuevo ?? fallback ?? 0);
    if (!Number.isFinite(valor) || valor < precioBase) return;
    setMejorOferta({ monto: valor, id_usuario: idUsuario ?? null });
  };

  /**
   * Botón "Sí, ofertar $X" del modal de "te superaron".
   *
   * Antes era `if (superado?.sugerido != null) confirmarOferta(...)` inline, y
   * ese `if` sin `else` era el bug: cuando el monto no venía, el botón no hacía
   * NADA — ni POST, ni mensaje, ni cerrar el modal. La persona apretaba y la
   * pantalla se quedaba igual, sin manera de saber si su oferta había entrado.
   * Un botón mudo es peor que uno que falla: al menos el que falla avisa.
   *
   * El monto se relee del estado por si el reintento anterior lo actualizó.
   */
  const reofertarSugerido = () => {
    const monto = Number(superado?.sugerido);
    if (!Number.isFinite(monto) || monto <= 0) {
      message.error('No pudimos calcular el monto siguiente. Actualizá la página e intentá de nuevo.');
      setSuperado(null);
      return;
    }
    confirmarOferta(monto);
  };

  const confirmarOferta = async (monto) => {
    setModalVisible(false);
    setOfertando(true);
    // El countdown de la ventana escucha esto: mientras el POST viaja no puede
    // recargar la pagina, o la persona se queda sin saber si su oferta entro.
    onOfertandoChange?.(true);
    try {
      const res = await ofertaService.create({
        id_evento: evento.id_evento,
        id_animal: animal.id_animal,
        monto_oferta: monto,
      });
      const oferta = res.data?.oferta;
      // La oferta entró: recién acá se descarta el monto guardado para el
      // reintento. Limpiarlo antes dejaba a `aceptarYOfertar` sin nada que
      // reintentar si el primer intento volvía a rebotar.
      setMontoPendiente(null);
      // El historial vive en AnimalPage, no acá: se le avisa para que las dos
      // columnas muestren lo mismo. `lidera` viaja con la oferta porque el
      // historial necesita saber si tiene que ir a buscar la que gano.
      onOfertaCreada?.(oferta, res.data?.lidera !== false);

      if (res.data?.lidera === false) {
        // Entró, pero otra oferta MÁS ALTA se metió en el medio. La de esta
        // persona es válida y queda registrada; lo que cambia es que ya no
        // lidera, así que se le ofrece el peldaño siguiente en vez de dejarla
        // creyendo que va ganando.
        actualizarMejorOferta(res.data.mejor_oferta, res.data.mejor_oferta_usuario, monto);
        // `monto_intentado` lo pone el front y no el backend: es el monto que
        // esta persona tipeó, y el modal lo necesita para decirle "tu oferta de
        // $X ya no es la más alta".
        setSuperado({ ...res.data, monto_intentado: monto });
      } else {
        actualizarMejorOferta(oferta?.monto_oferta, oferta?.id_usuario, monto);
        // Cierra el modal de "te superaron" si esta oferta vino de ahí: quedó
        // abierto a propósito durante el reintento para poder mostrar el spinner.
        setSuperado(null);
        message.success('¡Oferta registrada con éxito!');
      }
    } catch (err) {
      // Las condiciones son de ESTE remate y se aceptan una sola vez por
      // evento. No se piden en el login: ahí no hay remate del cual aceptar
      // nada. Acá sí, y además es el momento en que la plata está en juego.
      if (err.response?.data?.code === 'condiciones_pendientes') {
        setMontoPendiente(monto);
        try {
          const { data } = await buyerService.getCondiciones(evento.id_evento);
          setCondiciones(data);
          setCondicionesVisible(true);
        } catch {
          message.error('No pudimos cargar las condiciones del remate');
        }
      } else if (
        err.response?.data?.code === 'oferta_superada'
        // El monto no alcanza PORQUE YA HAY UNA MÁS ALTA: la persona armó su
        // oferta con lo que veía en pantalla y alguien llegó primero. Es el
        // mismo caso de "te superaron" y el más común de los tres —el 409 sale
        // solo de un race—, así que va al mismo modal en vez de a un cartel de
        // error. Se distingue por `sugerido`: sin ofertas previas el backend no
        // lo manda, y ahí sí es un error de carga (no llega al precio base).
        || (err.response?.data?.code === 'monto_insuficiente'
            && err.response?.data?.sugerido != null)
      ) {
        const data = err.response.data;
        actualizarMejorOferta(data.mejor_oferta, data.mejor_oferta_usuario, monto);
        setSuperado({ ...data, monto_intentado: monto });
      } else {
        const data = err.response?.data || {};
        message.error(data.error || 'Error al registrar la oferta');
        // El monto quedó viejo mientras el modal estaba abierto: alguien ofertó
        // en el medio y el backend devolvió el mínimo nuevo. Se refresca el
        // modal con ESE monto en vez de dejarlo ofreciendo el que acaba de
        // rebotar, que al segundo intento volvía a fallar igual — el clásico
        // botón que "no hace nada" dos veces seguidas.
        const nuevo = data.sugerido ?? data.minimo;
        if (superado && Number.isFinite(Number(nuevo))) {
          setSuperado((prev) => (prev ? { ...prev, sugerido: Number(nuevo) } : prev));
        }
      }
    } finally {
      setOfertando(false);
      // Se libera recién acá: el resultado ya se le mostró, así que si la
      // ventana cerró mientras tanto el countdown puede recargar tranquilo.
      onOfertandoChange?.(false);
    }
  };

  const aceptarYOfertar = async () => {
    setAceptando(true);
    const monto = montoPendiente;
    try {
      await buyerService.aceptarCondiciones(evento.id_evento);
      setCondicionesVisible(false);
      // Se reintenta la MISMA oferta que quedó trunca, para que la persona no
      // tenga que volver a tipear el monto. OJO: `montoPendiente` NO se limpia
      // acá. Si el reintento vuelve a chocar con `condiciones_pendientes` (otra
      // pestaña, réplica atrasada), confirmarOferta lo vuelve a setear y reabre
      // el modal; si lo borráramos en un finally, el segundo intento se quedaría
      // sin monto y el botón no haría absolutamente nada. Callejón sin salida.
      if (monto != null) {
        await confirmarOferta(monto);
      } else {
        // Sin monto no hay oferta que reintentar, pero la aceptación sí quedó
        // registrada: hay que decirlo, no cerrar el modal en silencio.
        message.success('Condiciones aceptadas. Ya podés ofertar.');
      }
    } catch (err) {
      message.error(err.response?.data?.error || 'No pudimos registrar tu aceptación');
    } finally {
      setAceptando(false);
    }
  };

  return (
    <Card
      style={{ marginBottom: 16, border: `2px solid ${colorPrimary}` }}
      styles={{ body: { padding: '16px 20px' } }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <DollarOutlined style={{ color: colorPrimary, fontSize: 20 }} />
        <Title level={5} style={{ margin: 0, color: colorPrimary, fontFamily: tipografia.title }}>
          {ofertasPendientes ? 'Las pre-ofertas todavía no abrieron'
            : ofertasCerradas ? 'El período de pre-ofertas cerró'
            : 'Pre-Oferta'}
        </Title>
      </div>

      {/* Precio de arriba SOLO cuando las ofertas estan abiertas (o no hay
          ventana configurada). Con ventana programada o cerrada, el precio va
          una sola vez, en el aviso de abajo: mostrarlo en los dos lados era
          repetir el mismo dato dos veces en la misma tarjeta. */}
      <div style={{ marginBottom: 12, display: (ofertasPendientes || ofertasCerradas) ? 'none' : undefined }}>
        {ofertaValida ? (
          <>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#1677ff' }}>
              ${mejorOferta.monto.toLocaleString('es-AR')}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Última oferta</Text>
              {yaLidero ? (
                // Se dice "sos vos" en vez del ID: el numero propio no lo tiene
                // nadie a mano, y el punto es que se entienda de un vistazo que
                // la oferta que va ganando es la suya.
                <Text style={{ fontSize: 12, fontWeight: 600, color: '#389e0d' }}>
                  — la tuya, estás liderando
                </Text>
              ) : mejorOferta.id_usuario ? (
                <Text type="secondary" style={{ fontSize: 11, color: '#bbb' }}>
                  — ID: {mejorOferta.id_usuario}
                </Text>
              ) : null}
            </div>
            <div style={{ marginTop: 6, color: '#999', fontSize: 12 }}>
              Precio de apertura: <span style={{ fontWeight: 600 }}>${precioBase.toLocaleString('es-AR')}</span>
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 22, fontWeight: 700, color: colorPrimary }}>
              ${precioBase.toLocaleString('es-AR')}
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>Precio base</Text>
          </>
        )}
        {salto > 0 && (
          <div style={{ marginTop: 4 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Salto de oferta: <strong>${salto.toLocaleString('es-AR')}</strong>
            </Text>
          </div>
        )}
      </div>

      {evento.es_finalizado ? (
        <div style={{
          background: '#fff8e1',
          border: '1px solid #ffe082',
          borderRadius: 8,
          padding: '14px 16px',
          textAlign: 'center',
          color: '#b45309',
          fontWeight: 600,
          fontSize: 14,
          fontFamily: tipografia.body,
        }}>
          🏁 Evento finalizado — las ofertas están cerradas
        </div>
      ) : (ofertasPendientes || ofertasCerradas) ? (
        /* Sin caja de color: ya estamos DENTRO del card con borde, y el titulo
           lo dice la cabecera. Una caja adentro de otra repetia el mismo
           mensaje dos veces. */
        <div style={{ fontFamily: tipografia.body }}>
          {ofertasPendientes && fmtMomento(estadoOfertas.desde) && (
            <div style={{ fontSize: 14, color: '#333' }}>
              Vas a poder pre-ofertar a partir del{' '}
              <strong>{fmtMomento(estadoOfertas.desde)}</strong>.
            </div>
          )}
          {ofertasPendientes && fmtMomento(estadoOfertas.hasta) && (
            <div style={{ fontSize: 13, color: '#666', marginTop: 2 }}>
              Cierran el {fmtMomento(estadoOfertas.hasta)}.
            </div>
          )}
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
            {bloquePrecioBase()}
          </div>
        </div>
      ) : bloqueadoPorAcceso ? (
        /* Habilitación pendiente. Se avisa ACÁ, con el cartel en lugar del
           formulario, en vez de dejar que escriba un monto y coma un 403. */
        <div style={{
          background: '#fff8e1',
          border: '1px solid #ffe082',
          borderRadius: 8,
          padding: '14px 16px',
          color: '#b45309',
          fontSize: 14,
          fontFamily: tipografia.body,
        }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>
            Todavía no podés ofertar
          </div>
          {acceso.solicitud_estado === 'pendiente'
            ? `Ya pediste acceso a ${acceso.cabana || 'la cabaña'}. En cuanto te habiliten vas a poder pre-ofertar en este remate.`
            : `${acceso.cabana || 'La cabaña'} tiene que habilitar tu usuario antes de que puedas pre-ofertar. Pedí el acceso desde “Mis remates”, en tu panel.`}
        </div>
      ) : (
        <Form form={form} onFinish={handleFormSubmit} layout="vertical">
          <Form.Item
            name="monto"
            label="Tu oferta"
            rules={[
              { required: true, message: 'Ingresá un monto' },
              { type: 'number', min: precioMin, message: `Mínimo $${precioMin.toLocaleString('es-AR')}` },
              // Las ofertas suben de a un salto exacto. El mensaje da los dos
              // peldaños vecinos: decir "es invalido" sin decir cual sirve
              // obliga a adivinar con la plata sobre la mesa.
              () => ({
                validator(_, value) {
                  if (value == null || salto <= 0 || enEscalon(value)) return Promise.resolve();
                  const proximo  = escalonSiguiente(value);
                  // El peldaño de abajo no puede quedar por debajo del mínimo:
                  // tipeando 700.000 con base 1.000.000 el anterior daba
                  // 500.000, y la persona leía dos mensajes que se contradicen
                  // (este ofreciendo un monto que la regla `min` ya rechaza).
                  const anterior = proximo - salto;
                  const opciones = anterior >= precioMin
                    ? `$${anterior.toLocaleString('es-AR')} o $${proximo.toLocaleString('es-AR')}`
                    : `$${Math.max(proximo, precioMin).toLocaleString('es-AR')}`;
                  return Promise.reject(new Error(
                    `Las ofertas suben de a $${salto.toLocaleString('es-AR')}. `
                    + `Podés ofertar ${opciones}`
                  ));
                },
              }),
            ]}
          >
            <InputNumber
              prefix="$"
              min={precioMin}
              step={salto || 1}
              style={{ width: '100%' }}
              size="large"
              // En utils/montoInput.js y no acá: el mismo formatter estaba
              // copiado en el panel del comprador con el bug del x100. Ver ahí
              // por qué la versión ingenua rompe con decimales.
              formatter={formatearMonto}
              parser={parsearMonto}
            />
          </Form.Item>
          {yaLidero && (
            // Nadie lo pujó: la oferta más alta ya es suya. Si oferta igual, se
            // sube el precio a sí misma. Va acá, pegado al botón, y no arriba
            // del todo: es lo último que lee antes de apretar.
            <div style={{
              marginBottom: 12, padding: '8px 12px', borderRadius: 8,
              background: '#f6ffed', border: '1px solid #b7eb8f',
              fontFamily: tipografia.body, fontSize: 12.5, color: '#254a12',
            }}>
              <strong>Ya estás liderando</strong> con ${mejorOferta.monto.toLocaleString('es-AR')}.
              Si ofertás de nuevo vas a superar tu propia oferta.
            </div>
          )}
          <Button
            type="primary"
            htmlType="submit"
            loading={ofertando}
            block
            size="large"
            style={{ background: colorBtn, border: 'none', borderRadius: 32, height: 'auto', padding: '12px 0', fontWeight: 700 }}
          >
            {isAuthenticated ? 'Registrar Oferta' : 'Iniciá sesión para ofertar'}
          </Button>

          {/* El dato viene en el mismo GET que la habilitacion; no usarlo seria
              traerlo para tirarlo. No BLOQUEA -aceptar en el modal al ofertar es
              un flujo corto y bueno- pero avisa antes, para que el paso no
              aparezca de sorpresa recien cuando ya escribio el monto. */}
          {isAuthenticated && acceso?.autorizado && acceso?.condiciones_aceptadas === false && (
            <div style={{ marginTop: 10, fontSize: 12, color: '#8a6d3b', fontFamily: tipografia.body }}>
              Antes de tu primera oferta te vamos a pedir que aceptes las
              condiciones de este remate.
            </div>
          )}
        </Form>
      )}

      {/* Modal de confirmación con mensajes del productor */}
      <Modal
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => confirmarOferta(montoConfirmar)}
        okText={yaLidero ? 'Sí, superar mi oferta' : 'Confirmar oferta'}
        cancelText="Cancelar"
        okButtonProps={{ style: { background: colorBtn, border: 'none' } }}
        title={
          <span style={{ color: yaLidero ? '#d46b08' : colorPrimary, fontFamily: tipografia.title }}>
            {yaLidero ? 'Ya estás liderando esta pre-oferta' : 'Confirmar pre-oferta'}
          </span>
        }
      >
        {yaLidero && (
          // Nadie la superó: la oferta más alta ya es suya. Se le dice con
          // todas las letras, con los dos montos a la vista, para que la
          // decisión sea deliberada y no un click de más.
          <p style={{ fontFamily: tipografia.body, fontSize: 14, color: '#333' }}>
            La oferta más alta de <strong>${mejorOferta.monto.toLocaleString('es-AR')}</strong>{' '}
            ya es tuya y nadie la superó. Si confirmás, vas a{' '}
            <strong>superar tu propia oferta</strong> y subir el precio a{' '}
            <strong>${Number(montoConfirmar || 0).toLocaleString('es-AR')}</strong>.
          </p>
        )}
        {montoConfirmar && (
          <div style={{ marginBottom: 16, textAlign: 'center' }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: colorPrimary }}>
              ${Number(montoConfirmar).toLocaleString('es-AR')}
            </div>
            <div style={{ fontSize: 13, color: '#888' }}>
              {animal.nom_animal} — {terminoLote(config).nombre} {animal.corral}
            </div>
          </div>
        )}
        {mensajeActivo && (
          <p style={{ fontFamily: tipografia.body, fontSize: 14, color: '#333', marginBottom: 0 }}>
            {interpolarMensaje(mensajeActivo, animal, evento)}
          </p>
        )}
      </Modal>

      {/* Te ganaron el peldaño. Re-oferta en un click, con el monto ya cargado:
          acá lo que corre son segundos, así que no se vuelve a pedir el texto de
          las condiciones (recién las aceptó) ni se le hace tipear de nuevo. */}
      <Modal
        open={!!superado}
        onCancel={() => setSuperado(null)}
        // El modal queda ABIERTO hasta que la oferta resuelve: cerrarlo antes
        // de llamar a confirmarOferta hacía que `confirmLoading` no llegara a
        // verse nunca, y en el único momento donde corren segundos la persona
        // apretaba y le desaparecía todo sin ninguna señal. Si el reintento
        // vuelve a rebotar, confirmarOferta reescribe `superado` y el modal se
        // queda con los datos nuevos.
        onOk={reofertarSugerido}
        okText={superado ? `Sí, ofertar $${Number(superado.sugerido).toLocaleString('es-AR')}` : 'Sí, ofertar'}
        cancelText="No, dejar así"
        confirmLoading={ofertando}
        okButtonProps={{ style: { background: colorBtn, border: 'none' } }}
        title={
          <span style={{ color: '#d4380d', fontFamily: tipografia.title }}>
            Te superaron la oferta
          </span>
        }
      >
        {superado && (
          <>
            {/* Texto redactado por el usuario. No se nombra el monto ajeno: lo
                que le importa a la persona es que ya no lidera y cuánto tiene
                que poner, no contra qué número perdió. */}
            <p style={{ fontFamily: tipografia.body, fontSize: 14, color: '#333' }}>
              Otro comprador lidera esta pre-oferta. Tu oferta de{' '}
              <strong>${Number(superado.monto_intentado ?? 0).toLocaleString('es-AR')}</strong>{' '}
              ya no es la más alta.
            </p>
            <div style={{
              textAlign: 'center', padding: '12px 0', marginTop: 8,
              background: '#fafafa', borderRadius: 8,
            }}>
              {/* En mayúscula, negrita y 2pt más grande que el resto de las
                  leyendas chicas: es la acción que se le está ofreciendo. */}
              <div style={{
                fontSize: 14, fontWeight: 700, textTransform: 'uppercase',
                letterSpacing: 0.4, color: '#555', marginBottom: 4,
              }}>
                Podés ofertar ahora
              </div>
              <div style={{ fontSize: 28, fontWeight: 700, color: colorPrimary }}>
                ${Number(superado.sugerido).toLocaleString('es-AR')}
              </div>
              <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                {animal.nom_animal} — {terminoLote(config).nombre} {animal.corral}
              </div>
            </div>
          </>
        )}
      </Modal>

      {/* Condiciones DE ESTE REMATE. Se aceptan una sola vez por evento, acá y
          no en el login: recién en este punto hay condiciones concretas que
          aceptar y el acto tiene consecuencia económica. */}
      <Modal
        open={condicionesVisible}
        onCancel={() => { setCondicionesVisible(false); setMontoPendiente(null); }}
        onOk={aceptarYOfertar}
        okText="Acepto y ofertar"
        cancelText="Cancelar"
        confirmLoading={aceptando}
        okButtonProps={{ style: { background: colorBtn, border: 'none' } }}
        width={720}
        title={
          <span style={{ color: colorPrimary, fontFamily: tipografia.title }}>
            Condiciones del remate
          </span>
        }
      >
        <p style={{ fontFamily: tipografia.body, fontSize: 13, color: '#666' }}>
          Antes de tu primera oferta en <strong>{condiciones?.nombre_evento || evento.nombre_evento}</strong>{' '}
          necesitamos que aceptes las condiciones que fija la cabaña para este remate.
        </p>

        {condiciones?.condiciones ? (
          <div style={{ maxHeight: '50vh', overflowY: 'auto', fontFamily: tipografia.body, fontSize: 14 }}>
            {[
              ['titulo_comercial', 'condiciones_comerciales'],
              ['titulo_sanitaria', 'condiciones_sanitarias'],
              ['titulo_reclamos',  'reclamos_garantias'],
            ].map(([kTitulo, kCuerpo]) => (
              condiciones.condiciones[kCuerpo] ? (
                <section key={kCuerpo} style={{ marginBottom: 20 }}>
                  <h4 style={{ color: colorPrimary, fontFamily: tipografia.title, marginBottom: 6 }}>
                    {condiciones.condiciones[kTitulo] || 'Condiciones'}
                  </h4>
                  {/* El backend guarda estas secciones como HTML, cargado por la
                      propia cabaña desde el panel. Se sanitiza igual: esto se
                      renderiza en el micrositio PÚBLICO, así que el markup
                      corre en el navegador de cualquier visitante. */}
                  <div {...htmlSanitizado(condiciones.condiciones[kCuerpo])} />
                </section>
              ) : null
            ))}
            {condiciones.condiciones.garantia && (
              <a
                // `urlSegura` decide, `fixUrl` arma. En ese orden: `garantia` es
                // un `db.String(1024)` de texto libre, y `fixUrl` devuelve los
                // `data:` VERBATIM —tiene que hacerlo, porque con esa misma
                // funcion se arma el `src` de las imagenes—, asi que era el
                // unico esquema que le pasaba de largo a este `<a>` publico.
                //
                // Un `javascript:` nunca llego a ejecutarse: `fixUrl` le
                // antepone el host y deja de estar al principio. Pero esa era
                // una garantia por accidente de estructura; ahora es explicita.
                //
                // Una ruta interna (`/uploads/...`) pasa `urlSegura` tal cual y
                // despues `fixUrl` le pone el host, como siempre.
                href={urlSegura(condiciones.condiciones.garantia)
                  ? fixUrl(condiciones.condiciones.garantia)
                  : undefined}
                target="_blank"
                rel="noopener noreferrer"
              >
                {condiciones.condiciones.titulo_garantia || 'Ver garantía (PDF)'}
              </a>
            )}
          </div>
        ) : (
          <p style={{ fontFamily: tipografia.body, fontSize: 14 }}>
            La cabaña todavía no cargó las condiciones de este remate. Al continuar
            dejás registrada tu conformidad con las que se publiquen.
          </p>
        )}
      </Modal>
    </Card>
  );
};

export default AnimalPage;
